<template>
  <div class="voicebot-page">

    <!-- ── Tab bar ──────────────────────────────────────────────────── -->
    <div class="vb-tabs">
      <div class="vb-tabs__left">
        <button
          v-for="tab in tabs" :key="tab.key"
          class="vb-tab" :class="{ 'vb-tab--active': activeTab === tab.key }"
          @click="activeTab = tab.key"
        >
          <feather-icon :icon="tab.icon" size="14" class="mr-50" />
          {{ tab.label }}
        </button>
      </div>
      <div class="vb-tabs__right">
        <span
          class="connection-badge"
          :class="health === true ? 'badge-connected' : health === false ? 'badge-error' : 'badge-checking'"
        >
          <span class="dot" />
          {{ health === true ? 'Connected' : health === false ? 'Error' : 'Checking…' }}
        </span>
      </div>
    </div>

    <!-- ════════════════════════════════════════════════════════════
         TAB: CALL HISTORY
    ════════════════════════════════════════════════════════════ -->
    <div v-if="activeTab === 'history'" class="vb-split">

      <!-- Left: call list -->
      <div class="vb-list" :style="!isMobile ? { width: listWidth + 'px' } : {}">
        <div class="vb-list-header">
          <h5 class="mb-0 font-weight-bold">Call History</h5>
          <div class="d-flex align-items-center" style="gap:4px">
            <b-button variant="flat-secondary" size="sm" class="btn-icon" :disabled="histLoading" @click="loadHistory()">
              <feather-icon icon="RefreshCwIcon" size="14" :class="histLoading ? 'spin' : ''" />
            </b-button>
            <div class="tag-manager-wrap" @click.stop>
              <b-button variant="flat-secondary" size="sm" class="btn-icon" @click.stop="toggleTagManager" v-b-tooltip.hover title="Manage Tags">
                <feather-icon icon="TagIcon" size="15" />
              </b-button>
              <!-- Tag manager dropdown -->
              <div v-if="showTagManager" class="tag-manager-panel">
                <div class="tag-manager-header">
                  <span class="font-weight-bold" style="font-size:0.9rem">Manage Tags</span>
                  <button class="tag-manager-close" @click="showTagManager = false">×</button>
                </div>
                <div class="tag-list-manage">
                  <div v-if="tagsLoading" class="text-center py-2">
                    <b-spinner small variant="primary" />
                  </div>
                  <div v-else-if="tags.length === 0" class="text-muted small text-center py-2">No tags yet</div>
                  <div v-for="tag in tags" :key="tag.id" class="tag-manage-item">
                    <span class="tag-pill" :style="{ background: tag.color }">{{ tag.name }}</span>
                    <button class="tag-delete-btn" @click="deleteTag(tag.id)" title="Delete tag">×</button>
                  </div>
                </div>
                <div class="tag-create-form">
                  <div class="tag-color-swatches">
                    <button
                      v-for="c in TAG_COLORS" :key="c"
                      class="tag-swatch"
                      :style="{ background: c, outline: newTagColor === c ? '2.5px solid ' + c : 'none', outlineOffset: '3px' }"
                      @click="newTagColor = c"
                    />
                    <label
                      class="tag-swatch tag-swatch-custom"
                      :style="isCustomTagColor ? { background: newTagColor, outline: '2.5px solid ' + newTagColor, outlineOffset: '3px' } : {}"
                      v-b-tooltip.hover title="Custom colour"
                    >
                      <feather-icon v-if="!isCustomTagColor" icon="PlusIcon" size="12" />
                      <input type="color" v-model="newTagColor" class="tag-swatch-custom-input" />
                    </label>
                  </div>
                  <div class="tag-create-row">
                    <input
                      v-model="newTagName"
                      class="tag-create-input"
                      placeholder="Tag name"
                      maxlength="20"
                      @keydown.enter.prevent="createTag"
                    />
                    <button class="tag-create-btn" :disabled="!newTagName.trim()" @click="createTag">Add</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="vb-list-search">
          <feather-icon icon="SearchIcon" size="14" class="vb-list-search__icon" />
          <input v-model="searchQuery" class="vb-list-search__input" placeholder="Search owner…" />
        </div>

        <!-- Tag filter bar -->
        <div v-if="tags.length > 0" class="tag-filter-bar">
          <button
            class="tag-filter-btn"
            :class="tagFilterId === null ? 'tag-filter-btn--all-active' : ''"
            @click="tagFilterId = null"
          >All</button>
          <button
            v-for="tag in tags" :key="tag.id"
            class="tag-filter-btn"
            :style="tagFilterId === tag.id ? { background: tag.color, color: '#fff', borderColor: tag.color } : {}"
            :class="tagFilterId === tag.id ? 'tag-filter-btn--active' : ''"
            @click="tagFilterId = tagFilterId === tag.id ? null : tag.id"
          >{{ tag.name }}</button>
        </div>

        <div class="vb-call-list">
          <div v-if="histLoading && history.length === 0" class="vb-empty">
            <b-spinner variant="warning" small />
            <div class="mt-50 text-muted small">Loading calls…</div>
          </div>
          <div v-else-if="filteredHistory.length === 0" class="vb-empty">
            <feather-icon icon="PhoneOffIcon" size="28" class="mb-50 text-muted" />
            <div>No calls found.</div>
          </div>
          <div
            v-for="item in filteredHistory" :key="item.id"
            class="vb-call-item"
            :class="{
              'vb-call-item--active': selectedCall && selectedCall.id === item.id,
              'vb-call-item--escalated': item.log && item.log.escalated,
            }"
            @click="selectCall(item)"
          >
            <div class="vb-call-icon">
              <b-avatar
                :text="avatarInitial(item.log && item.log.ownerName)"
                size="42" variant="light-warning"
              />
            </div>
            <div class="vb-call-body">
              <div class="vb-call-row1">
                <span class="vb-call-name">{{ (item.log && item.log.ownerName) || item.owner_id || 'Unknown' }}</span>
                <span class="vb-call-date">{{ formatDate(item.created_at) }}</span>
              </div>
              <div class="vb-call-row2">
                <span class="vb-lang-badge" :class="langClass(item.log && item.log.language)">
                  {{ langLabel(item.log && item.log.language) }}
                </span>
                <span v-if="item.log && item.log.escalated" class="vb-escalated-badge">Escalated</span>
                <span class="vb-call-turns">
                  <feather-icon icon="MessageCircleIcon" size="11" class="mr-25" />
                  {{ countTurns(item.log) }}
                </span>
                <span
                  v-for="tag in getOwnerTagObjects(item.owner_id).slice(0, 2)"
                  :key="tag.id"
                  class="tag-pill tag-pill--sm"
                  :style="{ background: tag.color }"
                >{{ tag.name }}</span>
                <span v-if="getOwnerTagObjects(item.owner_id).length > 2" class="tag-more">
                  +{{ getOwnerTagObjects(item.owner_id).length - 2 }}
                </span>
              </div>
            </div>
          </div>

        </div>

        <div v-if="totalHistory > histPerPage" class="vb-list-footer">
          <b-pagination
            v-model="histPage"
            :total-rows="totalHistory"
            :per-page="histPerPage"
            size="sm"
            align="center"
            class="mb-0"
            @input="loadHistory"
          />
        </div>
        <div v-else class="vb-list-footer">
          <small class="text-muted">{{ totalHistory }} call{{ totalHistory !== 1 ? 's' : '' }}</small>
        </div>
      </div>

      <!-- Resize handle (desktop only) -->
      <div class="vb-resize-handle" @mousedown="startResize" />

      <!-- Right: transcript panel -->
      <div class="vb-transcript" :class="{ 'is-open': showTranscript }">
        <div class="vb-sheet-handle d-block d-md-none" @click="showTranscript = !showTranscript" />

        <!-- Empty state -->
        <div v-if="!selectedCall" class="vb-no-selection">
          <feather-icon icon="PhoneIcon" size="40" class="text-warning mb-1" />
          <p class="font-weight-bold mb-25">Call Transcript</p>
          <small class="text-muted">Select a call from the list to view its transcript</small>
        </div>

        <template v-else>
          <!-- Header -->
          <div class="vb-transcript-header">
            <div class="d-flex align-items-center" style="gap:10px;min-width:0">
              <b-avatar
                :text="avatarInitial(selectedCall.log && selectedCall.log.ownerName)"
                size="36" variant="light-warning" class="flex-shrink-0"
              />
              <div style="min-width:0">
                <div class="d-flex align-items-center flex-wrap" style="gap:6px;row-gap:2px">
                  <span class="font-weight-bold text-truncate" style="max-width:180px">
                    {{ (selectedCall.log && selectedCall.log.ownerName) || selectedCall.owner_id || 'Unknown' }}
                  </span>
                  <!-- Tags assigned to this owner -->
                  <div v-if="getOwnerTagObjects(selectedCall.owner_id).length > 0" class="header-tags">
                    <span
                      v-for="tag in getOwnerTagObjects(selectedCall.owner_id)"
                      :key="tag.id"
                      class="tag-pill tag-pill--sm"
                      :style="{ background: tag.color }"
                    >{{ tag.name }}</span>
                  </div>
                </div>
                <div class="small text-muted d-flex align-items-center" style="gap:6px">
                  <span>{{ formatDate(selectedCall.created_at) }}</span>
                  <span>&bull;</span>
                  <span>{{ langLabel(selectedCall.log && selectedCall.log.language) }}</span>
                  <span v-if="selectedCall.log && selectedCall.log.callSid" style="font-family:monospace;font-size:0.68rem">
                    {{ shortSid(selectedCall.log.callSid) }}
                  </span>
                </div>
              </div>
            </div>
            <div class="d-flex align-items-center flex-shrink-0" style="gap:8px">
              <span v-if="selectedCall.log && selectedCall.log.escalated" class="vb-escalated-badge">Escalated</span>
              <!-- Tag assignment button -->
              <div class="tag-assign-wrap" @click.stop>
                <b-button
                  variant="flat-secondary"
                  size="sm"
                  class="btn-icon"
                  @click.stop="toggleTagPopup"
                  v-b-tooltip.hover
                  title="Tag this owner"
                >
                  <feather-icon
                    icon="TagIcon"
                    size="16"
                    :fill="getOwnerTagObjects(selectedCall.owner_id).length > 0 ? '#7367f0' : 'none'"
                    :style="getOwnerTagObjects(selectedCall.owner_id).length > 0 ? { color: '#7367f0' } : {}"
                  />
                </b-button>
                <div v-if="showTagPopup" class="tag-assign-dropdown">
                  <div class="tag-assign-header">Assign Tags</div>
                  <div v-if="tags.length === 0" class="text-muted small text-center" style="padding:8px 12px">
                    No tags yet. Create them using the <feather-icon icon="TagIcon" size="12" /> button in the list panel.
                  </div>
                  <label v-for="tag in tags" :key="tag.id" class="tag-assign-item">
                    <input
                      type="checkbox"
                      :checked="isOwnerTagged(selectedCall.owner_id, tag.id)"
                      @change="toggleOwnerTag(selectedCall.owner_id, tag.id)"
                    />
                    <span class="tag-pill" :style="{ background: tag.color }">{{ tag.name }}</span>
                  </label>
                </div>
              </div>
              <b-button variant="flat-secondary" size="sm" class="btn-icon" @click="closeTranscript">
                <feather-icon icon="XIcon" size="18" />
              </b-button>
            </div>
          </div>

          <!-- Transcript messages -->
          <div class="vb-transcript-body">
            <div v-if="!selectedCall.log || !selectedCall.log.transcript || selectedCall.log.transcript.length === 0" class="vb-empty">
              <feather-icon icon="MessageSquareIcon" size="32" class="text-muted mb-50" />
              <div class="text-muted small">No transcript available</div>
            </div>
            <div v-else class="vb-msg-list">
              <div
                v-for="(msg, i) in selectedCall.log.transcript" :key="i"
                class="vb-msg-row" :class="msg.role === 'Customer' ? 'vb-msg-row--customer' : 'vb-msg-row--bot'"
              >
                <div class="vb-msg-avatar">
                  <b-avatar
                    v-if="msg.role === 'Customer'"
                    :text="avatarInitial(selectedCall.log.ownerName)"
                    size="28" variant="light-secondary"
                  />
                  <b-avatar
                    v-else
                    size="28" variant="light-warning"
                    text="AI"
                  />
                </div>
                <div class="vb-msg-content" :class="msg.role === 'Customer' ? 'vb-msg-content--customer' : 'vb-msg-content--bot'">
                  <div class="vb-msg-role">{{ msg.role }}</div>
                  <div class="vb-bubble" :class="msg.role === 'Customer' ? 'vb-bubble--customer' : 'vb-bubble--bot'">
                    <div class="vb-msg-text">{{ msg.text }}</div>
                  </div>
                  <div v-if="msg.timestamp" class="vb-msg-time">{{ msg.timestamp }}</div>
                </div>
              </div>
            </div>
          </div>

          <!-- Properties footer -->
          <div v-if="selectedCall.log && selectedCall.log.properties && selectedCall.log.properties.length > 0" class="vb-props-footer">
            <div class="vb-props-title">
              <feather-icon icon="HomeIcon" size="13" class="mr-50 text-warning" />
              Properties ({{ selectedCall.log.properties.length }})
            </div>
            <div class="vb-prop-list">
              <div v-for="p in selectedCall.log.properties" :key="p.property_id || p.place_id" class="vb-prop-chip">
                <span class="vb-prop-unit">{{ p.units || p.title || '—' }}</span>
                <span v-if="p.place_name" class="vb-prop-place">{{ p.place_name }}</span>
                <span v-if="p.property_code" class="vb-prop-code">{{ p.property_code }}</span>
              </div>
            </div>
          </div>
        </template>
      </div>

      <!-- Mobile backdrop -->
      <div v-if="showTranscript" class="d-block d-md-none vb-backdrop" @click="showTranscript = false" />
    </div>

    <!-- ════════════════════════════════════════════════════════════
         TAB: MAKE A CALL
    ════════════════════════════════════════════════════════════ -->
    <div v-if="activeTab === 'call'" class="vb-form-wrap">
      <b-card class="vb-form-card">
        <div class="vb-form-title mb-1">
          <feather-icon icon="PhoneOutgoingIcon" size="18" class="mr-75 text-warning" />
          Trigger Outbound Call
        </div>
        <b-form @submit.prevent="triggerCall">
          <b-row>
            <b-col md="6">
              <b-form-group label="Owner ID" label-for="ownerId">
                <b-form-input
                  id="ownerId"
                  v-model="callForm.ownerId"
                  placeholder="e.g. 11743"
                  required
                />
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group label="Owner Name" label-for="ownerName">
                <b-form-input
                  id="ownerName"
                  v-model="callForm.ownerName"
                  placeholder="e.g. สมชาย ใจดี"
                  required
                />
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group label="Phone Numbers" label-for="phones" description="Comma-separated for multiple numbers">
                <b-form-input
                  id="phones"
                  v-model="callForm.phones"
                  placeholder="+6681234567, +6689876543"
                  required
                />
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group label="Language" label-for="language">
                <v-select
                  id="language"
                  v-model="callForm.language"
                  :options="languageOptions"
                  :reduce="o => o.value"
                  label="label"
                />
              </b-form-group>
            </b-col>
            <b-col md="12">
              <b-form-group label="Properties (JSON array)" label-for="properties">
                <b-form-textarea
                  id="properties"
                  v-model="callForm.propertiesRaw"
                  rows="5"
                  placeholder='[{"property_id": 14610, "units": "2701", "place_id": 1, "place_name": "185 ราชดำริ", "property_code": "P26043014610"}]'
                  class="font-monospace"
                  style="font-size:0.8rem"
                />
                <small v-if="propertiesError" class="text-danger">{{ propertiesError }}</small>
              </b-form-group>
            </b-col>
          </b-row>
          <div class="d-flex justify-content-end" style="gap:10px">
            <b-button variant="outline-secondary" type="button" @click="resetCallForm">Reset</b-button>
            <b-button variant="warning" type="submit" :disabled="calling">
              <b-spinner v-if="calling" small class="mr-50" />
              <feather-icon v-else icon="PhoneCallIcon" size="14" class="mr-50" />
              {{ calling ? 'Calling…' : 'Trigger Call' }}
            </b-button>
          </div>
        </b-form>


        <div v-if="liveCall" class="vb-call-result mt-1 vb-call-result--live">
          <b-spinner small class="mr-75" variant="warning" />
          <span>
            Call <strong>{{ liveCall.status }}</strong>
            <code class="ml-50">{{ liveCall.callSid }}</code>
          </span>
        </div>

        <div
          v-else-if="lastCallResult"
          class="vb-call-result mt-1"
          :class="{
            'vb-call-result--ok':   lastCallResult.ok,
            'vb-call-result--live': lastCallResult.warn,
            'vb-call-result--err':  !lastCallResult.ok && !lastCallResult.warn,
          }"
        >
          <feather-icon
            :icon="lastCallResult.ok ? 'CheckCircleIcon' : lastCallResult.warn ? 'AlertTriangleIcon' : 'AlertCircleIcon'"
            size="15" class="mr-75"
          />
          <template v-if="lastCallResult.finalStatus">
            Call ended: <strong>{{ lastCallResult.finalStatus }}</strong>
            <code class="ml-50">{{ lastCallResult.callSid }}</code>
          </template>
          <template v-else-if="lastCallResult.ok">
            Call dispatched! SID: <code>{{ lastCallResult.callSid }}</code>
          </template>
          <template v-else>
            Failed: {{ lastCallResult.error }}
          </template>
        </div>
      </b-card>
    </div>

    <!-- ════════════════════════════════════════════════════════════
         TAB: GROUP CALL
    ════════════════════════════════════════════════════════════ -->
    <div v-if="activeTab === 'group'" class="vb-form-wrap gc-page" @click="gcShowSuggestions = false">
      <div class="gc-layout">

        <!-- ╔══════════════════════════════╗
             ║  LEFT — Search + Results     ║
             ╚══════════════════════════════╝ -->
        <div class="gc-panel-left">
          <b-card no-body class="gc-card">

            <!-- Search header -->
            <div class="gc-header" @click.stop>
              <div class="gc-search-row">
                <div class="gc-search-box" :class="{ 'gc-search-box--focus': gcSearchFocus }">
                  <feather-icon icon="SearchIcon" size="16" class="gc-search-icon" />
                  <input
                    v-model="gcKeyword"
                    class="gc-search-input"
                    placeholder="Search by place, property code, or owner…"
                    autocomplete="off"
                    @focus="gcSearchFocus = true; gcSuggestions.length && (gcShowSuggestions = true)"
                    @blur="gcSearchFocus = false"
                    @input="onGcInput"
                    @keydown.enter.prevent="onGcEnter"
                    @keydown.down.prevent="gcActiveIdx < gcSuggestions.length - 1 && gcActiveIdx++"
                    @keydown.up.prevent="gcActiveIdx > 0 && gcActiveIdx--"
                    @keydown.escape="gcShowSuggestions = false"
                  />
                  <b-spinner v-if="gcLoading" small class="gc-search-spin" />
                  <button v-if="gcKeyword || hasColFilters" class="gc-search-clear" @click="clearGcSearch">
                    <feather-icon icon="XIcon" size="14" />
                  </button>
                </div>
                <button
                  class="gc-filter-btn"
                  :class="{ 'gc-filter-btn--on': gcShowFilters || activeFilterCount > 0 }"
                  @click.stop="gcShowFilters = !gcShowFilters"
                >
                  <feather-icon icon="SlidersIcon" size="15" />
                  <span class="gc-filter-btn-label">Filters</span>
                  <span v-if="activeFilterCount > 0" class="gc-filter-count">{{ activeFilterCount }}</span>
                </button>
              </div>

              <!-- Autocomplete dropdown -->
              <div v-if="gcShowSuggestions && gcSuggestions.length" class="gc-dropdown">
                <div
                  v-for="(item, idx) in gcSuggestions" :key="idx"
                  class="gc-dropdown-item"
                  :class="{ 'gc-dropdown-item--active': gcActiveIdx === idx }"
                  @mousedown.prevent="selectGcSuggestion(item)"
                >
                  <span :class="['gc-type-badge', 'gc-type-badge--' + (item.type || (item.place_id ? 'place' : 'owner'))]">
                    {{ gcSuggestionTypeLabel(item) }}
                  </span>
                  <span class="gc-dropdown-label">{{ item.label }}</span>
                </div>
              </div>

              <!-- Active filter chips -->
              <div v-if="gcSearchLabel || hasColFilters || gcTotal > 0" class="gc-context-row">
                <span v-if="gcSearchLabel" class="gc-context-pill gc-context-pill--location">
                  <feather-icon icon="MapPinIcon" size="11" class="mr-25" />
                  {{ gcSearchLabel }}
                  <button class="gc-pill-remove" @click.stop="clearGcSearch"><feather-icon icon="XIcon" size="10" /></button>
                </span>
                <span v-if="gcColProperty" class="gc-context-pill gc-context-pill--filter">
                  <feather-icon icon="HashIcon" size="11" class="mr-25" />Code: {{ gcColProperty }}
                  <button class="gc-pill-remove" @click.stop="gcColProperty = ''"><feather-icon icon="XIcon" size="10" /></button>
                </span>
                <span v-if="gcColBuilding" class="gc-context-pill gc-context-pill--filter">
                  <feather-icon icon="HomeIcon" size="11" class="mr-25" />{{ gcColBuilding }}
                  <button class="gc-pill-remove" @click.stop="gcColBuilding = ''"><feather-icon icon="XIcon" size="10" /></button>
                </span>
                <span v-if="gcColUnit" class="gc-context-pill gc-context-pill--filter">
                  Unit: {{ gcColUnit }}
                  <button class="gc-pill-remove" @click.stop="gcColUnit = ''"><feather-icon icon="XIcon" size="10" /></button>
                </span>
                <span v-if="gcColFloor" class="gc-context-pill gc-context-pill--filter">
                  Floor: {{ gcColFloor }}
                  <button class="gc-pill-remove" @click.stop="gcColFloor = ''"><feather-icon icon="XIcon" size="10" /></button>
                </span>
                <span v-if="gcColBedroom" class="gc-context-pill gc-context-pill--filter">
                  {{ gcColBedroom }} BR
                  <button class="gc-pill-remove" @click.stop="gcColBedroom = ''"><feather-icon icon="XIcon" size="10" /></button>
                </span>
                <span v-if="gcColOwner" class="gc-context-pill gc-context-pill--filter">
                  <feather-icon icon="UserIcon" size="11" class="mr-25" />{{ gcColOwner }}
                  <button class="gc-pill-remove" @click.stop="gcColOwner = ''"><feather-icon icon="XIcon" size="10" /></button>
                </span>
                <small v-if="!gcLoading && gcTotal > 0" class="gc-total-label">{{ gcTotal }} propert{{ gcTotal !== 1 ? 'ies' : 'y' }}</small>
              </div>
            </div>

            <!-- Filter panel -->
            <transition name="gc-slide">
              <div v-if="gcShowFilters" class="gc-filter-panel">
                <div class="gc-filter-grid">
                  <div class="gc-filter-field">
                    <label class="gc-filter-label">Property Code</label>
                    <b-form-input v-model="gcColProperty" placeholder="e.g. BKK001" size="sm" />
                  </div>
                  <div class="gc-filter-field">
                    <label class="gc-filter-label">Building</label>
                    <b-form-input v-model="gcColBuilding" placeholder="Building name" size="sm" />
                  </div>
                  <div class="gc-filter-field">
                    <label class="gc-filter-label">Unit</label>
                    <b-form-input v-model="gcColUnit" placeholder="e.g. 12A" size="sm" />
                  </div>
                  <div class="gc-filter-field">
                    <label class="gc-filter-label">Floor</label>
                    <b-form-input v-model="gcColFloor" placeholder="e.g. 5" size="sm" />
                  </div>
                  <div class="gc-filter-field">
                    <label class="gc-filter-label">Bedrooms</label>
                    <b-form-input v-model="gcColBedroom" placeholder="e.g. 2" size="sm" />
                  </div>
                  <div class="gc-filter-field">
                    <label class="gc-filter-label">Owner name</label>
                    <b-form-input v-model="gcColOwner" placeholder="Owner name" size="sm" />
                  </div>
                </div>
                <button v-if="activeFilterCount > 0" class="gc-clear-filter-btn" @click="clearColFilters">
                  <feather-icon icon="RotateCcwIcon" size="12" class="mr-25" />Clear filters
                </button>
              </div>
            </transition>

            <!-- Results header -->
            <div class="gc-results-header" v-if="gcTotal > 0 || gcLoading">
              <span class="gc-results-title">
                <b-spinner v-if="gcLoading" small variant="warning" class="mr-50" />
                <template v-if="!gcLoading">{{ gcTotal }} properties found</template>
              </span>
              <b-form-checkbox
                :checked="gcIsAllSelected"
                :indeterminate="gcActiveGroupHasAny && !gcIsAllSelected"
                @change="gcIsAllSelected ? gcDeselectAll() : gcSelectAll()"
                class="gc-select-all-chk"
              >
                <small>Select all</small>
              </b-form-checkbox>
            </div>

            <!-- Property list -->
            <div class="gc-list">
              <template v-if="gcLoading && gcResults.length === 0">
                <div v-for="i in 5" :key="'sk' + i" class="gc-item gc-item--skeleton">
                  <div class="gc-ske gc-ske--check" />
                  <div class="gc-item-body">
                    <div class="gc-ske gc-ske--title" />
                    <div class="gc-ske gc-ske--tags" />
                    <div class="gc-ske gc-ske--sub" />
                  </div>
                </div>
              </template>

              <div
                v-for="prop in gcResults" :key="prop.id"
                class="gc-item"
                :class="{
                  'gc-item--selected': gcActiveGroupIds.includes(prop.id),
                  'gc-item--taken':    gcOtherGroupIds.has(prop.id),
                }"
                @click="toggleGcSelect(prop.id)"
              >
                <b-form-checkbox
                  :checked="gcActiveGroupIds.includes(prop.id)"
                  class="gc-item-chk"
                  @change="toggleGcSelect(prop.id)"
                  @click.native.stop
                />
                <div class="gc-item-body">
                  <div class="gc-item-top">
                    <span class="gc-item-code">{{ prop.property_code }}</span>
                    <span v-if="prop.place_name" class="gc-item-place">{{ prop.place_name.en || prop.place_name.th || '' }}</span>
                    <span v-if="gcOtherGroupIds.has(prop.id)" class="gc-item-taken-badge">
                      {{ gcPropertyGroupMap[prop.id] }}
                    </span>
                  </div>
                  <div class="gc-item-tags">
                    <span v-if="prop.building" class="gc-tag gc-tag--building">{{ prop.building }}</span>
                    <span class="gc-tag">Unit {{ prop.units || prop.unit_no || '—' }}</span>
                    <span v-if="prop.floors" class="gc-tag">Floor {{ prop.floors }}</span>
                    <span v-if="prop.bedroom" class="gc-tag">{{ prop.bedroom }} BR</span>
                  </div>
                  <div class="gc-item-owner">
                    <feather-icon icon="UserIcon" size="11" class="gc-owner-icon" />
                    <span class="gc-owner-name">{{ prop.owner_firstname || '' }} {{ prop.owner_lastname || '' }}</span>
                    <template v-if="prop.owner_id in gcOwnerPhones">
                      <span v-if="gcOwnerPhones[prop.owner_id].length === 0" class="gc-no-phone">No phone</span>
                      <span v-for="(phone, pi) in gcOwnerPhones[prop.owner_id]" :key="pi" class="gc-phone">
                        <feather-icon icon="PhoneIcon" size="10" class="mr-25" />{{ phone }}
                      </span>
                    </template>
                    <b-spinner v-else-if="gcPhonesLoading" style="width:11px;height:11px;flex-shrink:0" />
                  </div>
                </div>
              </div>
            </div>

            <!-- Empty states -->
            <div v-if="!gcLoading && gcResults.length === 0 && (gcFilter || hasColFilters)" class="gc-empty">
              <feather-icon icon="InboxIcon" size="40" class="gc-empty-icon" />
              <p class="gc-empty-title">No properties found</p>
              <small class="gc-empty-sub">Try a different search term or adjust your filters</small>
            </div>
            <div v-else-if="!gcLoading && gcResults.length === 0 && !gcFilter && !hasColFilters" class="gc-empty">
              <feather-icon icon="PhoneCallIcon" size="40" class="gc-empty-icon" />
              <p class="gc-empty-title">Ready to group call</p>
              <small class="gc-empty-sub">Search by place name, property code, or use the filters above</small>
            </div>

            <!-- Pagination -->
            <div v-if="gcTotal > gcPerPage" class="gc-pagination-row">
              <b-pagination
                v-model="gcPage"
                :total-rows="gcTotal"
                :per-page="gcPerPage"
                size="sm"
                align="center"
                class="mb-0"
              />
            </div>

          </b-card>
        </div>

        <!-- ╔══════════════════════════════╗
             ║  RIGHT — Group Manager       ║
             ╚══════════════════════════════╝ -->
        <div class="gc-panel-right">
          <div class="gc-groups-card">

            <!-- Header -->
            <div class="gc-groups-header">
              <span class="gc-groups-title">
                <feather-icon icon="LayersIcon" size="15" class="mr-50 text-warning" />
                Group Calls
                <span v-if="gcGroups.length > 0" class="gc-groups-count-badge">{{ gcGroups.length }}</span>
              </span>
              <button class="gc-add-group-btn" @click="createGroup">
                <feather-icon icon="PlusIcon" size="13" />
                New group
              </button>
            </div>

            <!-- Empty state -->
            <div v-if="gcGroups.length === 0" class="gc-groups-empty">
              <feather-icon icon="LayersIcon" size="36" class="gc-groups-empty-icon" />
              <p class="gc-groups-empty-title">No groups yet</p>
              <small class="gc-groups-empty-sub">Create a group, then tick properties from the left to add them</small>
              <button class="gc-add-group-btn mt-1" @click="createGroup">
                <feather-icon icon="PlusIcon" size="13" />
                Create first group
              </button>
            </div>

            <template v-else>
              <!-- Tab strip — one chip per group -->
              <div class="gc-tabs-strip">
                <button
                  v-for="group in gcGroups" :key="group.id"
                  class="gc-tab"
                  :class="{ 'gc-tab--active': group.id === gcActiveGroupId }"
                  :title="group.name"
                  @click="setActiveGroup(group.id)"
                >
                  <span class="gc-tab-dot" :class="{
                    'gc-tab-dot--done':    group.status === 'done',
                    'gc-tab-dot--calling': group.status === 'calling',
                    'gc-tab-dot--failed':  group.status === 'failed',
                  }" />
                  <span class="gc-tab-name">{{ group.name }}</span>
                  <span class="gc-tab-count" :class="group.items.length >= 20 ? 'gc-tab-count--full' : ''">{{ group.items.length }}</span>
                </button>
              </div>

              <!-- Active group panel -->
              <div v-if="gcActiveGroup" class="gc-group-panel">

                <!-- Panel head: name + status + actions -->
                <div class="gc-group-panel-head">
                  <input
                    v-if="gcEditingGroupId === gcActiveGroup.id"
                    :ref="'gcNameInput_' + gcActiveGroup.id"
                    v-model="gcEditingGroupName"
                    class="gc-group-panel-name-input"
                    maxlength="50"
                    @keydown.enter.prevent="saveGroupName(gcActiveGroup)"
                    @keydown.esc.prevent="gcEditingGroupId = null"
                    @blur="saveGroupName(gcActiveGroup)"
                  />
                  <span
                    v-else
                    class="gc-group-panel-name"
                    title="Double-click to rename"
                    @dblclick="startEditGroupName(gcActiveGroup)"
                  >{{ gcActiveGroup.name }}</span>

                  <span v-if="gcActiveGroup.status === 'calling'" class="gc-group-status-label gc-group-status-label--calling">
                    <b-spinner style="width:9px;height:9px" class="mr-25" />Calling
                  </span>
                  <span v-else-if="gcActiveGroup.status === 'done'" class="gc-group-status-label gc-group-status-label--done">Done</span>
                  <span v-else-if="gcActiveGroup.status === 'failed'" class="gc-group-status-label gc-group-status-label--failed">Failed</span>

                  <button class="gc-icon-btn" title="Rename group" @click="startEditGroupName(gcActiveGroup)">
                    <feather-icon icon="EditIcon" size="13" />
                  </button>
                  <button class="gc-icon-btn gc-icon-btn--danger" title="Delete group" @click="deleteGroup(gcActiveGroup.id)">
                    <feather-icon icon="Trash2Icon" size="13" />
                  </button>
                </div>

                <!-- Scrollable body -->
                <div class="gc-group-panel-body">

                  <!-- Property list inside group -->
                  <div class="gc-group-props">
                    <div v-if="gcActiveGroup.items.length === 0" class="gc-group-props-empty">
                      <feather-icon icon="MousePointerIcon" size="16" class="mb-25" />
                      <div>Tick properties on the left to add here</div>
                    </div>
                    <div
                      v-for="prop in gcActiveGroup.items" :key="prop.id"
                      class="gc-group-prop-row"
                    >
                      <div class="gc-group-prop-info">
                        <span class="gc-group-prop-code">{{ prop.property_code }}</span>
                        <span class="gc-group-prop-sub">
                          {{ prop.place_name && (prop.place_name.en || prop.place_name.th) || '' }}
                          <template v-if="prop.units || prop.unit_no"> · Unit {{ prop.units || prop.unit_no }}</template>
                        </span>
                      </div>
                      <button class="gc-group-prop-remove" @click="removeFromGroup(gcActiveGroup.id, prop.id)">
                        <feather-icon icon="XIcon" size="11" />
                      </button>
                    </div>
                  </div>

                  <!-- Limit warning -->
                  <div v-if="gcActiveGroup.items.length >= 20" class="gc-group-limit-warn">
                    <feather-icon icon="AlertCircleIcon" size="12" class="mr-25" />
                    Max 20 properties per group
                  </div>

                  <!-- Per-call live status list -->
                  <div v-if="gcActiveGroup.callStatuses && gcActiveGroup.callStatuses.length > 0" class="gc-call-statuses">
                    <div class="gc-call-statuses-title">
                      <feather-icon icon="PhoneIcon" size="11" class="mr-25" />
                      Individual Call Status
                    </div>
                    <div v-for="(c, i) in gcActiveGroup.callStatuses" :key="i" class="gc-call-status-row">
                      <span class="gc-call-status-name">{{ c.ownerName || '—' }}</span>
                      <span class="gc-call-status-phone">{{ c.phone }}</span>
                      <span class="gc-call-status-badge" :class="gcStatusClass(c.status)">
                        <b-spinner
                          v-if="c.callSid && !isTerminalStatus(c.status)"
                          style="width:7px;height:7px;margin-right:4px;flex-shrink:0"
                        />
                        {{ c.status }}
                      </span>
                    </div>
                  </div>

                  <!-- All-calls-done summary -->
                  <div v-if="gcActiveGroup.callSummary" class="gc-call-summary">
                    <feather-icon icon="CheckCircleIcon" size="13" class="mr-50 flex-shrink-0" />
                    <span class="flex-fill">
                      All {{ gcActiveGroup.callSummary.total }} calls finished —
                      <strong>{{ gcActiveGroup.callSummary.completed }}</strong> completed
                      <template v-if="gcActiveGroup.callSummary.notAnswered > 0"> · {{ gcActiveGroup.callSummary.notAnswered }} not answered</template>
                      <template v-if="gcActiveGroup.callSummary.failed > 0"> · {{ gcActiveGroup.callSummary.failed }} failed</template>
                    </span>
                    <span v-if="gcActiveGroup.callSummary.finishedAt" class="gc-call-summary-time">
                      {{ formatDateTime(gcActiveGroup.callSummary.finishedAt) }}
                    </span>
                  </div>

                </div>

                <!-- Footer: single call action -->
                <div class="gc-group-panel-footer">
                  <button
                    class="gc-call-btn"
                    :class="{ 'gc-call-btn--done': gcActiveGroup.status === 'done' }"
                    :disabled="gcActiveGroup.calling || gcActiveGroup.items.length === 0 || gcActiveGroup.status === 'done'"
                    :title="gcActiveGroup.status === 'done' ? 'Group already called' : 'Trigger group call'"
                    @click="triggerGroupCall(gcActiveGroup)"
                  >
                    <b-spinner v-if="gcActiveGroup.calling" small class="mr-50" />
                    <feather-icon v-else-if="gcActiveGroup.status === 'done'" icon="CheckIcon" size="14" class="mr-50" />
                    <feather-icon v-else icon="PhoneCallIcon" size="14" class="mr-50" />
                    <template v-if="gcActiveGroup.status === 'done'">Group called</template>
                    <template v-else-if="gcActiveGroup.calling">Calling…</template>
                    <template v-else>Call {{ gcActiveGroup.items.length }} propert{{ gcActiveGroup.items.length === 1 ? 'y' : 'ies' }}</template>
                  </button>
                  <div
                    v-if="gcActiveGroup.result"
                    class="gc-group-result"
                    :class="gcActiveGroup.result.ok ? 'gc-group-result--ok' : 'gc-group-result--err'"
                  >
                    <feather-icon :icon="gcActiveGroup.result.ok ? 'CheckCircleIcon' : 'AlertCircleIcon'" size="12" class="mr-25 flex-shrink-0" />
                    <span v-if="gcActiveGroup.result.ok">
                      <strong>{{ gcActiveGroup.result.dispatched }}</strong> dispatched
                      <template v-if="gcActiveGroup.result.failed > 0"> · {{ gcActiveGroup.result.failed }} failed</template>
                      <template v-if="gcActiveGroup.result.skipped > 0"> · {{ gcActiveGroup.result.skipped }} skipped</template>
                    </span>
                    <span v-else>{{ gcActiveGroup.result.error }}</span>
                  </div>
                </div>

              </div>
            </template>

          </div>
        </div>

      </div>
    </div>

    <!-- ════════════════════════════════════════════════════════════
         TAB: SYSTEM PROMPT
    ════════════════════════════════════════════════════════════ -->
    <div v-if="activeTab === 'prompt'" class="vb-form-wrap">
      <b-card class="vb-form-card">
        <div class="d-flex align-items-center justify-content-between mb-1">
          <div class="vb-form-title">
            <feather-icon icon="CpuIcon" size="18" class="mr-75 text-warning" />
            AI System Prompt
          </div>
          <b-button variant="flat-secondary" size="sm" :disabled="promptLoading" @click="loadPrompt">
            <feather-icon icon="RefreshCwIcon" size="14" :class="promptLoading ? 'spin' : ''" />
          </b-button>
        </div>

        <div v-if="promptLoading" class="text-center py-3">
          <b-spinner variant="warning" small />
        </div>
        <template v-else>
          <b-form-textarea
            v-model="promptText"
            rows="20"
            class="font-monospace"
            placeholder="Loading system prompt…"
            :disabled="!promptEditing"
            style="font-size:0.8rem;line-height:1.65"
          />
          <div class="d-flex justify-content-end mt-1" style="gap:10px">
            <b-button v-if="!promptEditing" variant="outline-warning" @click="promptEditing = true">
              <feather-icon icon="EditIcon" size="14" class="mr-50" />Edit
            </b-button>
            <template v-else>
              <b-button variant="outline-secondary" @click="cancelEditPrompt">Cancel</b-button>
              <b-button variant="warning" :disabled="promptSaving" @click="savePrompt">
                <b-spinner v-if="promptSaving" small class="mr-50" />
                {{ promptSaving ? 'Saving…' : 'Save Prompt' }}
              </b-button>
            </template>
          </div>
        </template>
      </b-card>
    </div>

  </div>
</template>

<script>
import voicebotAxios from '@/libs/voicebot-axios'
import chatbotAxios from '@/libs/chatbot-axios'
import axios from '@axios'
import {
  BCard, BButton, BAvatar, BSpinner, BFormCheckbox,
  BForm, BFormGroup, BFormInput, BFormTextarea, BRow, BCol,
  BPagination,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  components: {
    BCard, BButton, BAvatar, BSpinner, BFormCheckbox,
    BForm, BFormGroup, BFormInput, BFormTextarea, BRow, BCol,
    BPagination,
    vSelect,
  },

  data() {
    return {
      activeTab: 'history',
      tabs: [
        { key: 'history', label: 'Call History',   icon: 'PhoneCallIcon' },
        // { key: 'call',    label: 'Make a Call',    icon: 'PhoneOutgoingIcon' },
        { key: 'group',   label: 'Group Call',     icon: 'UsersIcon' },
        { key: 'prompt',  label: 'System Prompt',  icon: 'CpuIcon' },
      ],
      health: null,

      // ── History tab ───────────────────────────────────────────
      histLoading: false,
      history: [],
      totalHistory: 0,
      histPage: 1,
      histPerPage: 20,
      searchQuery: '',
      selectedCall: null,
      showTranscript: false,
      listWidth: 340,
      isResizing: false,

      // ── Tags (shared with chat bot) ────────────────────────────
      tags: [],
      ownerTags: {},
      tagsLoading: false,
      showTagManager: false,
      showTagPopup: false,
      newTagName: '',
      newTagColor: '#7367f0',
      tagFilterId: null,
      TAG_COLORS: [
        '#7367f0', '#28a745', '#ea5455', '#ff9f43',
        '#00cfe8', '#1e9af3', '#9b59b6', '#e91e63',
      ],

      // ── Make a Call tab ───────────────────────────────────────
      calling: false,
      lastCallResult: null,
      propertiesError: '',
      liveCall: null,
      callForm: {
        ownerId: '',
        ownerName: '',
        phones: '',
        language: 'th',
        propertiesRaw: '',
      },
      languageOptions: [
        { label: 'Thai (th)', value: 'th' },
        { label: 'English (en)', value: 'en' },
      ],

      // ── Group Call tab ────────────────────────────────────────
      gcKeyword: '',
      gcSuggestions: [],
      gcShowSuggestions: false,
      gcActiveIdx: -1,
      gcDebounceTimer: null,
      gcFilter: null,       // { place_id } | { propertyfilter } | { building } | { owner_id }
      gcSearchLabel: '',
      gcResults: [],
      gcTotal: 0,
      gcPage: 1,
      gcPerPage: 20,
      gcLoading: false,
      gcGroups: [],
      gcActiveGroupId: null,
      gcNextGroupId: 1,
      gcGroupsLoaded: false,
      gcEditingGroupId: null,
      gcEditingGroupName: '',
      gcOwnerPhones: {},      // owner_id → string[]
      gcGroupPolls: {},       // groupId → intervalId
      gcPhonesLoading: false,
      gcSearchFocus: false,
      gcShowFilters: false,
      // inline column filters
      gcColProperty: '',
      gcColOwner: '',
      gcColUnit: '',
      gcColFloor: '',
      gcColBuilding: '',
      gcColBedroom: '',
      gcColFilterTimer: null,

      // ── System Prompt tab ──────────────────────────────────────
      promptLoading: false,
      promptSaving: false,
      promptText: '',
      promptOriginal: '',
      promptEditing: false,
    }
  },

  computed: {
    isMobile() {
      return typeof window !== 'undefined' && window.innerWidth < 768
    },
    isCustomTagColor() {
      return !this.TAG_COLORS.includes(this.newTagColor)
    },
    gcActiveGroup() {
      return this.gcGroups.find(g => g.id === this.gcActiveGroupId) || null
    },
    gcActiveGroupIds() {
      return this.gcActiveGroup ? this.gcActiveGroup.items.map(p => p.id) : []
    },
    gcOtherGroupIds() {
      const ids = new Set()
      this.gcGroups
        .filter(g => g.id !== this.gcActiveGroupId)
        .forEach(g => g.items.forEach(p => ids.add(p.id)))
      return ids
    },
    gcPropertyGroupMap() {
      const map = {}
      this.gcGroups.forEach(g => {
        g.items.forEach(p => { map[p.id] = g.name })
      })
      return map
    },
    gcIsAllSelected() {
      if (!this.gcActiveGroup || this.gcResults.length === 0) return false
      return this.gcResults.every(p => this.gcActiveGroupIds.includes(p.id))
    },
    gcActiveGroupHasAny() {
      return this.gcActiveGroup ? this.gcActiveGroup.items.length > 0 : false
    },
    hasColFilters() {
      return !!(this.gcColProperty || this.gcColOwner || this.gcColUnit ||
        this.gcColFloor || this.gcColBuilding || this.gcColBedroom)
    },
    activeFilterCount() {
      return [this.gcColProperty, this.gcColOwner, this.gcColUnit,
        this.gcColFloor, this.gcColBuilding, this.gcColBedroom].filter(Boolean).length
    },
    filteredHistory() {
      let items = this.history
      if (this.tagFilterId) {
        items = items.filter(item =>
          (this.ownerTags[item.owner_id] || []).some(t => t.id === this.tagFilterId),
        )
      }
      const q = this.searchQuery.trim().toLowerCase()
      if (!q) return items
      return items.filter(item => {
        const name = ((item.log && item.log.ownerName) || item.owner_id || '').toLowerCase()
        return name.includes(q)
      })
    },
  },

  watch: {
    activeTab(tab) {
      if (tab === 'prompt' && !this.promptText) this.loadPrompt()
      if (tab === 'group' && !this.gcGroupsLoaded) this.loadGroups()
    },
    showTagManager(val) {
      if (val) {
        document.addEventListener('click', this._closeTagManagerOutside)
      } else {
        document.removeEventListener('click', this._closeTagManagerOutside)
      }
    },
    showTagPopup(val) {
      if (val) {
        document.addEventListener('click', this._closeTagPopupOutside)
      } else {
        document.removeEventListener('click', this._closeTagPopupOutside)
      }
    },
    gcColProperty() { this.debouncedGcColFetch() },
    gcColOwner()    { this.debouncedGcColFetch() },
    gcColUnit()     { this.debouncedGcColFetch() },
    gcColFloor()    { this.debouncedGcColFetch() },
    gcColBuilding() { this.debouncedGcColFetch() },
    gcColBedroom()  { this.debouncedGcColFetch() },
    gcPage(val, old) {
      if (val !== old && (this.gcFilter || this.gcColProperty || this.gcColOwner ||
        this.gcColUnit || this.gcColFloor || this.gcColBuilding || this.gcColBedroom)) {
        this.fetchGcResults()
      }
    },
  },

  mounted() {
    this._gcSaveTimers = {}
    this.checkHealth()
    this.loadHistory()
    this.loadGroups()
    this.loadTags()
  },

  beforeDestroy() {
    this.stopResize()
    this.stopLiveCall()
    Object.keys(this.gcGroupPolls).forEach(id => this.stopGroupPolling(id))
    document.removeEventListener('click', this._closeTagManagerOutside)
    document.removeEventListener('click', this._closeTagPopupOutside)
  },

  methods: {
    // ── Health ────────────────────────────────────────────────────
    async checkHealth() {
      try {
        await voicebotAxios.get('/health')
        this.health = true
      } catch {
        this.health = false
      }
    },

    // ── History ───────────────────────────────────────────────────
    async loadHistory(page) {
      if (page) this.histPage = page
      this.histLoading = true
      try {
        const { data } = await voicebotAxios.get('/api/call-history', {
          params: {
            limit: this.histPerPage,
            offset: (this.histPage - 1) * this.histPerPage,
          },
        })
        this.history = data.data.map(row => ({ ...row, log: this.parseLog(row.log) }))
        this.totalHistory = Number(data.total) || 0
        this.health = true
        this.loadOwnerTagsForList()
      } catch (e) {
        this.health = false
        this.showToast('Failed to load call history: ' + e.message, 'danger')
      } finally {
        this.histLoading = false
      }
    },

    parseLog(raw) {
      if (!raw) return null
      if (typeof raw === 'object') return raw
      try { return JSON.parse(raw) } catch { return null }
    },

    selectCall(item) {
      this.selectedCall = item
      this.showTranscript = true
      if (item.owner_id) this.loadOwnerTags(item.owner_id)
    },

    closeTranscript() {
      this.showTranscript = false
      this.selectedCall = null
    },

    countTurns(log) {
      if (!log || !log.transcript) return 0
      return log.transcript.length
    },

    // ── Tags (shared with chat bot) ────────────────────────────────
    toggleTagManager() {
      this.showTagManager = !this.showTagManager
      if (this.showTagManager) this.showTagPopup = false
    },

    toggleTagPopup() {
      this.showTagPopup = !this.showTagPopup
      if (this.showTagPopup) this.showTagManager = false
    },

    _closeTagManagerOutside() {
      this.showTagManager = false
    },

    _closeTagPopupOutside() {
      this.showTagPopup = false
    },

    async loadTags() {
      this.tagsLoading = true
      try {
        const { data } = await chatbotAxios.get('/api/v1/tags')
        this.tags = data.data || data || []
      } catch (e) {
        console.warn('Failed to load tags:', e.message)
      } finally {
        this.tagsLoading = false
      }
    },

    async loadOwnerTags(ownerId) {
      try {
        const { data } = await chatbotAxios.get(`/api/v1/conversations/${ownerId}/tags`)
        this.$set(this.ownerTags, ownerId, data.data || data || [])
      } catch (e) {
        console.warn('Failed to load owner tags:', e.message)
      }
    },

    loadOwnerTagsForList() {
      const ownerIds = [...new Set(this.history.map(item => item.owner_id).filter(Boolean))]
      ownerIds.forEach(id => this.loadOwnerTags(id))
    },

    async createTag() {
      const name = this.newTagName.trim()
      if (!name) return
      try {
        const { data } = await chatbotAxios.post('/api/v1/tags', { name, color: this.newTagColor })
        const tag = data.data || data
        this.tags = [...this.tags, tag]
        this.newTagName = ''
      } catch (e) {
        this.showToast('Failed to create tag: ' + e.message, 'danger')
      }
    },

    async deleteTag(id) {
      try {
        await chatbotAxios.delete(`/api/v1/tags/${id}`)
        this.tags = this.tags.filter(t => t.id !== id)
        const updated = {}
        Object.keys(this.ownerTags).forEach(oid => {
          updated[oid] = this.ownerTags[oid].filter(t => t.id !== id)
        })
        this.ownerTags = updated
      } catch (e) {
        this.showToast('Failed to delete tag: ' + e.message, 'danger')
      }
    },

    async toggleOwnerTag(ownerId, tagId) {
      const current = this.ownerTags[ownerId] || []
      const isTagged = current.some(t => t.id === tagId)
      if (isTagged) {
        try {
          await chatbotAxios.delete(`/api/v1/conversations/${ownerId}/tags/${tagId}`)
          this.$set(this.ownerTags, ownerId, current.filter(t => t.id !== tagId))
        } catch (e) {
          this.showToast('Failed to remove tag: ' + e.message, 'danger')
        }
      } else {
        try {
          await chatbotAxios.post(`/api/v1/conversations/${ownerId}/tags`, { tagId })
          const tag = this.tags.find(t => t.id === tagId)
          if (tag) this.$set(this.ownerTags, ownerId, [...current, tag])
        } catch (e) {
          this.showToast('Failed to add tag: ' + e.message, 'danger')
        }
      }
    },

    isOwnerTagged(ownerId, tagId) {
      return (this.ownerTags[ownerId] || []).some(t => t.id === tagId)
    },

    getOwnerTagObjects(ownerId) {
      return this.ownerTags[ownerId] || []
    },

    // ── Make a Call ───────────────────────────────────────────────
    async triggerCall() {
      this.propertiesError = ''
      let properties = []
      const raw = this.callForm.propertiesRaw.trim()
      if (raw) {
        try { properties = JSON.parse(raw) } catch {
          this.propertiesError = 'Invalid JSON — please check the properties array.'
          return
        }
      }

      const phones = this.callForm.phones
        .split(',')
        .map(p => p.trim())
        .filter(Boolean)

      this.calling = true
      this.lastCallResult = null
      try {
        const { data } = await voicebotAxios.post('/api/outbound-call', {
          ownerId:   this.callForm.ownerId,
          ownerName: this.callForm.ownerName,
          phones,
          properties,
          language:  this.callForm.language,
        })
        const sid = data.callSid || data.sid
        this.lastCallResult = { ok: true, callSid: sid }
        this.showToast('Call dispatched!', 'success')
        if (sid) this.startLiveCall(sid)
      } catch (e) {
        const msg = e.response?.data?.error || e.message
        this.lastCallResult = { ok: false, error: msg }
        this.showToast('Call failed: ' + msg, 'danger')
      } finally {
        this.calling = false
      }
    },

    resetCallForm() {
      this.callForm = { ownerId: '', ownerName: '', phones: '', language: 'th', propertiesRaw: '' }
      this.lastCallResult = null
      this.propertiesError = ''
    },

    // ── System Prompt ─────────────────────────────────────────────
    async loadPrompt() {
      this.promptLoading = true
      try {
        const { data } = await voicebotAxios.get('/api/prompt')
        this.promptText = data.prompt
        this.promptOriginal = data.prompt
      } catch (e) {
        this.showToast('Failed to load prompt: ' + e.message, 'danger')
      } finally {
        this.promptLoading = false
      }
    },

    async savePrompt() {
      this.promptSaving = true
      try {
        await voicebotAxios.post('/api/prompt', { prompt: this.promptText })
        this.promptOriginal = this.promptText
        this.promptEditing = false
        this.showToast('System prompt updated!', 'success')
      } catch (e) {
        this.showToast('Failed to save prompt: ' + e.message, 'danger')
      } finally {
        this.promptSaving = false
      }
    },

    cancelEditPrompt() {
      this.promptText = this.promptOriginal
      this.promptEditing = false
    },

    // ── Resize (desktop) ──────────────────────────────────────────
    startResize(e) {
      this.isResizing = true
      this._startX = e.clientX
      this._startWidth = this.listWidth
      document.addEventListener('mousemove', this.onResize)
      document.addEventListener('mouseup', this.stopResize)
      document.body.style.cursor = 'col-resize'
      document.body.style.userSelect = 'none'
    },

    onResize(e) {
      if (!this.isResizing) return
      const newWidth = Math.min(520, Math.max(240, this._startWidth + (e.clientX - this._startX)))
      this.listWidth = newWidth
    },

    stopResize() {
      this.isResizing = false
      document.removeEventListener('mousemove', this.onResize)
      document.removeEventListener('mouseup', this.stopResize)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    },

    // ── Group Call ────────────────────────────────────────────────
    onGcInput() {
      clearTimeout(this.gcDebounceTimer)
      this.gcActiveIdx = -1
      const kw = this.gcKeyword.trim()
      if (kw.length < 2) { this.gcSuggestions = []; this.gcShowSuggestions = false; return }
      this.gcDebounceTimer = setTimeout(async () => {
        try {
          const { data } = await axios.post('v2/search/property', { keyword: kw })
          this.gcSuggestions = data.data || []
          this.gcShowSuggestions = this.gcSuggestions.length > 0
        } catch { this.gcSuggestions = []; this.gcShowSuggestions = false }
      }, 300)
    },

    resetGcPage() {
      this.gcPage = 1
      this.gcSelected = []
      this.gcCallResult = null
    },

    onGcEnter() {
      if (this.gcActiveIdx >= 0 && this.gcSuggestions[this.gcActiveIdx]) {
        this.selectGcSuggestion(this.gcSuggestions[this.gcActiveIdx])
      } else {
        this.gcShowSuggestions = false
        this.gcFilter = { propertyfilter: this.gcKeyword.trim() }
        this.gcSearchLabel = this.gcKeyword.trim()
        this.resetGcPage()
        this.fetchGcResults()
      }
    },

    selectGcSuggestion(item) {
      this.gcKeyword = item.label
      this.gcShowSuggestions = false
      this.gcSearchLabel = item.label

      if (item.type === 'code') {
        this.gcFilter = { propertyfilter: item.value }
      } else if (item.type === 'building') {
        this.gcFilter = { building: item.value }
      } else if (item.place_id) {
        this.gcFilter = { place_id: item.place_id }
      } else if (item.owner_id) {
        this.gcFilter = { owner_id: item.owner_id }
      } else {
        this.gcFilter = { propertyfilter: item.label }
      }

      this.resetGcPage()
      this.fetchGcResults()
    },

    clearGcSearch() {
      this.gcKeyword = ''
      this.gcFilter = null
      this.gcSearchLabel = ''
      this.gcSuggestions = []
      this.gcShowSuggestions = false
      this.gcResults = []
      this.gcTotal = 0
      this.gcPage = 1
      this.gcSelected = []
      this.gcSelectedItems = []
      this.gcCallResult = null
      this.gcOwnerPhones = {}
      this.gcColProperty = ''
      this.gcColOwner    = ''
      this.gcColUnit     = ''
      this.gcColFloor    = ''
      this.gcColBuilding = ''
      this.gcColBedroom  = ''
    },

    gcSuggestionTypeLabel(item) {
      if (item.type === 'code')     return 'Code'
      if (item.type === 'building') return 'Building'
      if (item.place_id)            return 'Place'
      if (item.owner_id)            return 'Owner'
      return 'Search'
    },

    onGcPageChange(page) {
      this.gcPage = page
      this.fetchGcResults()
    },

    // Normalizes to E.164, defaulting to Thailand (+66) for local-format numbers.
    // Owner phone numbers come from the CRM as raw strings (e.g. "0944959428")
    // and Twilio silently mis-routes or rejects them without a leading "+66".
    formatPhoneNumber(phone) {
      const cleaned = String(phone || '').replace(/[\s\-().]/g, '')
      if (!cleaned) return ''
      if (cleaned.startsWith('+')) return cleaned
      if (cleaned.startsWith('00')) return `+${cleaned.substring(2)}`
      if (cleaned.startsWith('0')) return `+66${cleaned.substring(1)}`
      return `+66${cleaned}`
    },

    clearColFilters() {
      this.gcColProperty = ''
      this.gcColOwner    = ''
      this.gcColUnit     = ''
      this.gcColFloor    = ''
      this.gcColBuilding = ''
      this.gcColBedroom  = ''
    },

    debouncedGcColFetch() {
      clearTimeout(this.gcColFilterTimer)
      this.gcColFilterTimer = setTimeout(() => {
        if (this.gcPage === 1) {
          this.fetchGcResults()   // page unchanged, watcher won't fire
        } else {
          this.gcPage = 1         // watcher fires and calls fetchGcResults
        }
      }, 350)
    },

    async fetchGcResults() {
      const hasFilter = this.gcFilter || this.gcColProperty || this.gcColOwner ||
        this.gcColUnit || this.gcColFloor || this.gcColBuilding || this.gcColBedroom
      if (!hasFilter) return
      this.gcLoading = true
      try {
        const { data } = await axios.post('v2/property/filter', {
          page: this.gcPage,
          perpage: this.gcPerPage,
          is_approve: 1,
          ...(this.gcFilter || {}),
          ...(this.gcColProperty && { propertyfilter: this.gcColProperty }),
          ...(this.gcColOwner    && { ownerfilter:    this.gcColOwner }),
          ...(this.gcColUnit     && { unit:            this.gcColUnit }),
          ...(this.gcColFloor    && { floor:           this.gcColFloor }),
          ...(this.gcColBuilding && { building:        this.gcColBuilding }),
          ...(this.gcColBedroom  && { bedrooms:        this.gcColBedroom }),
        })
        const rows = data.data || []
        this.gcResults = rows
        this.gcTotal = Number(data.total ?? data.totalRecords ?? 0) || rows.length
        // v2/property/filter already returns phones[] per row — populate cache directly
        rows.forEach(p => {
          if (p.owner_id && !(p.owner_id in this.gcOwnerPhones)) {
            const raw = p.phones
            // API returns phones as: array of objects, a single object, a comma string, or undefined
            let list = []
            if (Array.isArray(raw)) {
              list = raw.map(ph => (typeof ph === 'object' ? ph.phone_no : ph)).filter(Boolean)
            } else if (raw && typeof raw === 'object' && raw.phone_no) {
              list = [raw.phone_no]
            } else if (typeof raw === 'string' && raw) {
              list = raw.split(',').map(s => s.trim()).filter(Boolean)
            }
            this.$set(this.gcOwnerPhones, p.owner_id, list.map(this.formatPhoneNumber))
          }
        })
      } catch (e) {
        this.showToast('Search failed: ' + e.message, 'danger')
      } finally {
        this.gcLoading = false
      }
    },

    // ── Group management ─────────────────────────────────────────
    async loadGroups() {
      try {
        const { data } = await voicebotAxios.get('/api/group-calls')
        this.gcGroups = data.data.map(g => ({
          id:           g.id,
          name:         g.name,
          items:        g.items || [],
          status:       g.status || 'draft',
          calling:      false,
          result:       g.result || null,
          // result._callStatuses / result._callSummary are the most recently polled values
          // and are always more up-to-date than the top-level fields (which the backend
          // may only update at dispatch time, keeping the initial "queued" state).
          callStatuses: g.result?._callStatuses || g.callStatuses || null,
          callSummary:  g.result?._callSummary  || g.callSummary  || null,
        }))
        this.gcNextGroupId = this.gcGroups.length + 1
        const draft = this.gcGroups.find(g => g.status === 'draft')
        this.gcActiveGroupId = draft ? draft.id : (this.gcGroups.length ? this.gcGroups[this.gcGroups.length - 1].id : null)
        this.gcGroupsLoaded = true

        // Resume polling for any group whose calls weren't all finished before the refresh
        const TERMINAL = ['completed', 'busy', 'failed', 'no-answer', 'canceled']
        this.gcGroups.forEach(g => {
          if (g.callStatuses && g.callStatuses.some(c => c.callSid && !TERMINAL.includes(c.status))) {
            this.startGroupPolling(g)
          }
        })
      } catch (e) {
        console.error('Failed to load groups', e)
      }
    },

    debouncedSaveGroup(groupId) {
      clearTimeout(this._gcSaveTimers[groupId])
      this._gcSaveTimers[groupId] = setTimeout(async () => {
        const group = this.gcGroups.find(g => g.id === groupId)
        if (group) await voicebotAxios.put(`/api/group-calls/${groupId}`, { items: group.items })
      }, 600)
    },

    async createGroup() {
      const name = `Group ${this.gcNextGroupId++}`
      try {
        const { data } = await voicebotAxios.post('/api/group-calls', { name, items: [] })
        this.gcGroups.push({ id: data.id, name, items: [], status: 'draft', calling: false, result: null })
        this.gcActiveGroupId = data.id
      } catch (e) {
        this.showToast('Failed to create group: ' + e.message, 'danger')
      }
    },

    setActiveGroup(id) {
      this.gcActiveGroupId = id
    },

    startEditGroupName(group) {
      this.gcEditingGroupId = group.id
      this.gcEditingGroupName = group.name
      this.$nextTick(() => {
        const input = this.$refs['gcNameInput_' + group.id]
        if (input) { const el = Array.isArray(input) ? input[0] : input; el.focus(); el.select() }
      })
    },

    async saveGroupName(group) {
      const name = this.gcEditingGroupName.trim() || group.name
      this.gcEditingGroupId = null
      if (name === group.name) return
      group.name = name
      await voicebotAxios.put(`/api/group-calls/${group.id}`, { name })
    },

    async deleteGroup(id) {
      try {
        await voicebotAxios.delete(`/api/group-calls/${id}`)
      } catch { /* ignore */ }
      const idx = this.gcGroups.findIndex(g => g.id === id)
      if (idx !== -1) this.gcGroups.splice(idx, 1)
      if (this.gcActiveGroupId === id) {
        this.gcActiveGroupId = this.gcGroups.length ? this.gcGroups[this.gcGroups.length - 1].id : null
      }
    },

    removeFromGroup(groupId, propId) {
      const group = this.gcGroups.find(g => g.id === groupId)
      if (!group) return
      const idx = group.items.findIndex(p => p.id === propId)
      if (idx !== -1) {
        group.items.splice(idx, 1)
        this.debouncedSaveGroup(groupId)
      }
    },

    async toggleGcSelect(propId) {
      if (this.gcOtherGroupIds.has(propId)) return
      if (!this.gcActiveGroupId) await this.createGroup()
      const group = this.gcActiveGroup
      if (!group) return
      const idx = group.items.findIndex(p => p.id === propId)
      if (idx === -1) {
        if (group.items.length >= 20) {
          this.showToast('Maximum 20 properties per group. Create a new group to continue.', 'warning')
          return
        }
        const prop = this.gcResults.find(p => p.id === propId)
        if (prop) {
          group.items.push(this.trimProp(prop))
          this.debouncedSaveGroup(group.id)
        }
      } else {
        group.items.splice(idx, 1)
        this.debouncedSaveGroup(group.id)
      }
    },

    async gcSelectAll() {
      if (!this.gcActiveGroupId) await this.createGroup()
      const group = this.gcActiveGroup
      if (!group) return
      const available = 20 - group.items.length
      this.gcResults
        .filter(p => !group.items.find(s => s.id === p.id) && !this.gcOtherGroupIds.has(p.id))
        .slice(0, available)
        .forEach(p => group.items.push(this.trimProp(p)))
      this.debouncedSaveGroup(group.id)
    },

    gcDeselectAll() {
      if (this.gcActiveGroup) {
        this.gcActiveGroup.items = []
        this.debouncedSaveGroup(this.gcActiveGroup.id)
      }
    },

    async fetchGcOwnerPhones() {
      const ownerIds = [...new Set(this.gcResults.map(p => p.owner_id))]
        .filter(id => !(id in this.gcOwnerPhones))
      if (!ownerIds.length) return
      this.gcPhonesLoading = true
      await Promise.all(ownerIds.map(async id => {
        try {
          const { data } = await axios.get(`property/v1/owner/${id}`)
          const record = Array.isArray(data.data) ? data.data[0] : data.data
          const raw = record?.phones || record?.phone
          const list = Array.isArray(raw) ? raw
            : typeof raw === 'string' && raw ? [raw] : []
          this.$set(this.gcOwnerPhones, id, list.map(this.formatPhoneNumber))
        } catch {
          this.$set(this.gcOwnerPhones, id, [])
        }
      }))
      this.gcPhonesLoading = false
    },

    async triggerGroupCall(group) {
      if (!group || group.items.length === 0) return
      group.calling = true
      group.result = null
      group.status = 'calling'
      await voicebotAxios.put(`/api/group-calls/${group.id}`, { status: 'calling' })

      // Ensure phones are loaded for every owner in this group
      const missingOwnerIds = [...new Set(group.items.map(p => p.owner_id))]
        .filter(id => !(id in this.gcOwnerPhones))
      if (missingOwnerIds.length > 0) {
        await Promise.all(missingOwnerIds.map(async id => {
          try {
            const { data } = await axios.get(`property/v1/owner/${id}`)
            const rows = Array.isArray(data.data) ? data.data : []
            // filterOwner returns multiple results — find the one with matching id
            const record = rows.find(r => String(r.id) === String(id)) || rows[0]
            const raw = record?.phones || record?.phone
            const list = Array.isArray(raw) ? raw
              : typeof raw === 'string' && raw ? raw.split(',').map(s => s.trim()).filter(Boolean)
              : []
            this.$set(this.gcOwnerPhones, id, list.map(this.formatPhoneNumber))
          } catch {
            this.$set(this.gcOwnerPhones, id, [])
          }
        }))
      }

      const ownerMap = {}
      for (const prop of group.items) {
        if (!ownerMap[prop.owner_id]) {
          ownerMap[prop.owner_id] = {
            ownerId:    String(prop.owner_id),
            ownerName:  `${prop.owner_firstname || ''} ${prop.owner_lastname || ''}`.trim(),
            phones:     this.gcOwnerPhones[prop.owner_id] || [],
            properties: [],
            language:   'th',
          }
        }
        ownerMap[prop.owner_id].properties.push({
          property_id:   prop.id,
          units:         prop.units || prop.unit_no || '—',
          place_id:      prop.place_id,
          place_name:    prop.place_name?.en || prop.place_name || '',
          property_code: prop.property_code,
        })
      }

      const calls   = Object.values(ownerMap).filter(o => o.phones.length > 0)
      const skipped = Object.values(ownerMap).filter(o => o.phones.length === 0)

      if (calls.length === 0) {
        group.result = { ok: false, error: 'No phone numbers found for selected owners.' }
        group.status = 'failed'
        group.calling = false
        await voicebotAxios.put(`/api/group-calls/${group.id}`, { status: 'failed', result: group.result })
        return
      }

      try {
        const { data } = await voicebotAxios.post('/api/bulk-outbound-call', { calls })
        const results = data.results || []
        const failed = results.filter(r => r.error).length

        group.result = { ok: true, dispatched: calls.length - failed, failed, skipped: skipped.length }
        group.status = 'done'

        // Build per-call status tracking
        const callStatuses = calls.map((c, i) => ({
          ownerName: c.ownerName,
          phone:     c.phones[0] || '',
          callSid:   results[i]?.callSid || null,
          status:    results[i]?.error ? 'failed' : 'queued',
          error:     results[i]?.error || null,
        }))
        this.$set(group, 'callStatuses', callStatuses)
        this.$set(group, 'callSummary', null)

        if (callStatuses.some(c => c.callSid)) {
          this.startGroupPolling(group)
        }

        this.showToast(`${calls.length - failed} call(s) dispatched!`, 'success')
        await voicebotAxios.put(`/api/group-calls/${group.id}`, {
          status: 'done',
          result: group.result,
          callStatuses: group.callStatuses,
        })
      } catch (e) {
        group.result = { ok: false, error: e.response?.data?.error || e.message }
        group.status = 'failed'
        await voicebotAxios.put(`/api/group-calls/${group.id}`, { status: 'failed', result: group.result })
      } finally {
        group.calling = false
      }
    },

    // ── Live call status polling ──────────────────────────────────
    TERMINAL_STATUSES: ['completed', 'busy', 'failed', 'no-answer', 'canceled'],

    startLiveCall(callSid) {
      this.stopLiveCall()
      this.liveCall = { callSid, status: 'queued' }
      this.liveCall._errCount = 0
      this.liveCall.timer = setInterval(() => this.pollCallStatus(callSid), 3000)
    },

    stopLiveCall() {
      if (this.liveCall?.timer) {
        clearInterval(this.liveCall.timer)
      }
      this.liveCall = null
    },

    // ── Group call live-status polling ────────────────────────────
    startGroupPolling(group) {
      this.stopGroupPolling(group.id)
      const TERMINAL = ['completed', 'busy', 'failed', 'no-answer', 'canceled']
      this.gcGroupPolls[group.id] = setInterval(async () => {
        const statuses = group.callStatuses
        if (!statuses) { this.stopGroupPolling(group.id); return }

        const pending = statuses.filter(c => c.callSid && !TERMINAL.includes(c.status))
        if (pending.length === 0) { this.stopGroupPolling(group.id); return }

        await Promise.all(pending.map(async c => {
          try {
            const { data } = await voicebotAxios.get(`/api/call-status/${c.callSid}`)
            c._errCount = 0
            c.status = data.status || data.callStatus || c.status
          } catch {
            c._errCount = (c._errCount || 0) + 1
            // After ~50 s of consecutive errors assume call ended without a status update
            if (c._errCount >= 10) c.status = 'no-answer'
          }
        }))

        const allDone = statuses.every(c => !c.callSid || TERMINAL.includes(c.status))
        if (allDone) {
          this.stopGroupPolling(group.id)
          const completed   = statuses.filter(c => c.status === 'completed').length
          const notAnswered = statuses.filter(c => ['busy', 'no-answer', 'canceled'].includes(c.status)).length
          const failedCnt   = statuses.filter(c => c.status === 'failed').length
          const summary = { completed, notAnswered, failed: failedCnt, total: statuses.length, finishedAt: new Date().toISOString() }
          this.$set(group, 'callSummary', summary)
          this.showToast(
            `All ${statuses.length} calls finished — ${completed} completed`,
            completed === statuses.length ? 'success' : 'warning',
          )
          // Embed summary + statuses inside result so they survive even if the backend
          // doesn't store top-level callSummary / callStatuses fields.
          const mergedResult = { ...(group.result || {}), _callSummary: summary, _callStatuses: statuses }
          group.result = mergedResult
          voicebotAxios.put(`/api/group-calls/${group.id}`, {
            result:       mergedResult,
            callStatuses: statuses,
            callSummary:  summary,
          }).catch(() => {})
        } else {
          // Embed updated statuses inside result for the same reason
          const mergedResult = { ...(group.result || {}), _callStatuses: statuses }
          group.result = mergedResult
          voicebotAxios.put(`/api/group-calls/${group.id}`, {
            result:       mergedResult,
            callStatuses: statuses,
          }).catch(() => {})
        }
      }, 5000)
    },

    stopGroupPolling(groupId) {
      if (this.gcGroupPolls[groupId]) {
        clearInterval(this.gcGroupPolls[groupId])
        delete this.gcGroupPolls[groupId]
      }
    },

    isTerminalStatus(status) {
      return ['completed', 'busy', 'failed', 'no-answer', 'canceled'].includes(status)
    },

    gcStatusClass(status) {
      const map = {
        queued:        'gc-call-status-badge--pending',
        initiated:     'gc-call-status-badge--pending',
        ringing:       'gc-call-status-badge--pending',
        'in-progress': 'gc-call-status-badge--active',
        completed:     'gc-call-status-badge--completed',
        failed:        'gc-call-status-badge--failed',
        busy:          'gc-call-status-badge--warn',
        'no-answer':   'gc-call-status-badge--warn',
        canceled:      'gc-call-status-badge--warn',
      }
      return map[status] || 'gc-call-status-badge--pending'
    },

    formatDateTime(iso) {
      if (!iso) return ''
      const d = new Date(iso)
      const pad = n => String(n).padStart(2, '0')
      return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`
    },

    async pollCallStatus(callSid) {
      if (!this.liveCall) return
      try {
        const { data } = await voicebotAxios.get(`/api/call-status/${callSid}`)
        if (!this.liveCall) return
        this.liveCall._errCount = 0
        const status = data.status || data.callStatus || 'unknown'
        this.liveCall.status = status

        const TERMINAL = ['completed', 'busy', 'failed', 'no-answer', 'canceled']
        if (TERMINAL.includes(status)) {
          const WARN = ['busy', 'no-answer', 'canceled']
          this.lastCallResult = {
            ok: status === 'completed',
            warn: WARN.includes(status),
            callSid,
            finalStatus: status,
          }
          this.stopLiveCall()
          const variants = { completed: 'success', busy: 'warning', failed: 'danger', 'no-answer': 'warning', canceled: 'secondary' }
          this.showToast(`Call ${status}`, variants[status] || 'info')
          if (status === 'completed') setTimeout(() => this.loadHistory(), 2000)
        }
      } catch {
        if (!this.liveCall) return
        this.liveCall._errCount = (this.liveCall._errCount || 0) + 1
        // Only give up after 15 consecutive errors (~45 s) — a single error should not kill polling
        if (this.liveCall._errCount >= 15) this.stopLiveCall()
      }
    },

    // ── Helpers ───────────────────────────────────────────────────
    trimProp(p) {
      return {
        id: p.id, property_code: p.property_code,
        place_id: p.place_id, place_name: p.place_name,
        units: p.units, unit_no: p.unit_no,
        floors: p.floors, bedroom: p.bedroom, building: p.building,
        owner_id: p.owner_id, owner_firstname: p.owner_firstname, owner_lastname: p.owner_lastname,
      }
    },
    avatarInitial(name) { return name ? name[0].toUpperCase() : '?' },
    shortSid(sid) { return sid ? sid.slice(0, 12) + '…' : '' },

    langLabel(lang) {
      if (lang === 'th') return 'Thai'
      if (lang === 'en') return 'English'
      return 'Unknown'
    },

    langClass(lang) {
      if (lang === 'th') return 'vb-lang--th'
      if (lang === 'en') return 'vb-lang--en'
      return 'vb-lang--unknown'
    },

    formatDate(raw) {
      if (!raw) return '—'
      const d = new Date(raw)
      const dd  = String(d.getDate()).padStart(2, '0')
      const mm  = String(d.getMonth() + 1).padStart(2, '0')
      const yy  = String(d.getFullYear()).slice(-2)
      const hh  = String(d.getHours()).padStart(2, '0')
      const min = String(d.getMinutes()).padStart(2, '0')
      return `${dd}/${mm}/${yy}, ${hh}:${min}`
    },

    showToast(message, variant = 'success') {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: message,
          icon: variant === 'success' ? 'CheckIcon' : 'AlertCircleIcon',
          variant,
        },
      })
    },
  },
}
</script>

<style lang="scss" scoped>
// ── Page ──────────────────────────────────────────────────────────────────────
.voicebot-page {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 180px);
  min-height: 500px;
  border-radius: 0.428rem;
  overflow: hidden;
  box-shadow: 0 4px 24px 0 rgba(34, 41, 47, 0.1);
  background: #fff;

  @media (max-width: 767px) {
    height: calc(100dvh - 130px);
    min-height: unset;
  }
}

// ── Tab bar ───────────────────────────────────────────────────────────────────
.vb-tabs {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 16px;
  border-bottom: 1px solid #ebe9f1;
  background: #fff;
  flex-shrink: 0;
  gap: 4px;
}

.vb-tabs__left {
  display: flex;
  align-items: center;
  gap: 2px;
}

.vb-tabs__right {
  display: flex;
  align-items: center;
}

.vb-tab {
  display: flex;
  align-items: center;
  padding: 13px 14px;
  border: none;
  background: transparent;
  font-size: 0.875rem;
  font-weight: 500;
  color: #6e6b7b;
  cursor: pointer;
  border-bottom: 2px solid transparent;
  transition: color 0.15s, border-color 0.15s;
  white-space: nowrap;

  &:hover { color: #f0a500; }

  &--active {
    color: #f0a500;
    border-bottom-color: #f0a500;
    font-weight: 600;
  }
}

// ── Connection badge ──────────────────────────────────────────────────────────
.connection-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 0.73rem;
  font-weight: 600;
}
.badge-connected { background: #d4edda; color: #155724; }
.badge-error     { background: #f8d7da; color: #721c24; }
.badge-checking  { background: #fff3cd; color: #856404; }

.dot {
  width: 6px; height: 6px; border-radius: 50%;
  display: inline-block; flex-shrink: 0;
}
.badge-connected .dot { background: #28a745; }
.badge-error .dot     { background: #dc3545; }
.badge-checking .dot  { background: #f0a500; }

// ── Split layout ──────────────────────────────────────────────────────────────
.vb-split {
  display: flex;
  flex: 1;
  overflow: hidden;

  @media (max-width: 767px) {
    flex-direction: column;
  }
}

// ── Left list panel ───────────────────────────────────────────────────────────
.vb-list {
  width: 340px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  border-right: 1px solid #ebe9f1;
  overflow: hidden;

  @media (max-width: 767px) {
    width: 100% !important;
    border-right: none;
    flex: 1;
    height: 0;
  }
}

.vb-list-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 18px 12px;
  flex-shrink: 0;
}

.vb-list-search {
  position: relative;
  padding: 0 12px 10px;
  flex-shrink: 0;
}
.vb-list-search__icon {
  position: absolute;
  left: 24px;
  top: 50%;
  transform: translateY(-65%);
  color: #adb5bd;
  pointer-events: none;
}
.vb-list-search__input {
  width: 100%;
  border: 1px solid #e8e8ee;
  border-radius: 20px;
  padding: 7px 14px 7px 34px;
  font-size: 0.85rem;
  background: #f7f7fc;
  outline: none;
  color: #3d3d3d;
  transition: border-color 0.2s, box-shadow 0.2s;

  &:focus {
    border-color: #f0a500;
    background: #fff;
    box-shadow: 0 0 0 3px rgba(240, 165, 0, 0.12);
  }
}

.vb-call-list {
  flex: 1;
  overflow-y: auto;
}

.vb-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 16px;
  color: #adb5bd;
  font-size: 0.85rem;
  text-align: center;
}

.vb-call-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  cursor: pointer;
  border-bottom: 1px solid #f5f5f7;
  transition: background 0.12s;
  position: relative;

  &:hover { background: #fffbf0; }

  &--active {
    background: #fff8e1;
    border-left: 3px solid #f0a500;
    padding-left: 13px;

    &:hover { background: #fff3cd; }
  }

  &--escalated .vb-call-name {
    color: #c82333;
  }
}

.vb-call-icon { flex-shrink: 0; }

.vb-call-body {
  flex: 1;
  min-width: 0;
}

.vb-call-row1 {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 6px;
  margin-bottom: 4px;
}

.vb-call-name {
  font-size: 0.9rem;
  font-weight: 500;
  color: #3d3d3d;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.vb-call-date {
  font-size: 0.7rem;
  color: #adb5bd;
  white-space: nowrap;
  flex-shrink: 0;
}

.vb-call-row2 {
  display: flex;
  align-items: center;
  gap: 5px;
  flex-wrap: wrap;
}

.vb-lang-badge {
  display: inline-flex;
  align-items: center;
  padding: 1px 7px;
  border-radius: 10px;
  font-size: 0.7rem;
  font-weight: 600;
}
.vb-lang--th      { background: #fff3cd; color: #856404; }
.vb-lang--en      { background: #cce5ff; color: #004085; }
.vb-lang--unknown { background: #f0f0f0; color: #6c757d; }

.vb-escalated-badge {
  display: inline-flex;
  align-items: center;
  padding: 1px 7px;
  border-radius: 10px;
  font-size: 0.7rem;
  font-weight: 700;
  background: #f8d7da;
  color: #721c24;
}

.vb-call-turns {
  font-size: 0.7rem;
  color: #adb5bd;
  display: flex;
  align-items: center;
}

.vb-list-footer {
  padding: 6px 16px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-top: 1px solid #f0f0f5;
  flex-shrink: 0;
  background: #fafafa;
}

// ── Resize handle ─────────────────────────────────────────────────────────────
.vb-resize-handle {
  width: 20px;
  flex-shrink: 0;
  cursor: col-resize;
  background: transparent;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  transition: background 0.2s;

  &:hover { background: rgba(240, 165, 0, 0.08); }

  &::before {
    content: '';
    position: absolute;
    top: 0; bottom: 0;
    left: 50%; transform: translateX(-50%);
    width: 3px; border-radius: 3px;
    background: #dee2e6;
    transition: background 0.2s, width 0.2s;
  }

  &::after {
    content: '';
    position: absolute;
    top: 50%; left: 50%;
    transform: translate(-50%, -50%);
    width: 4px; height: 32px; border-radius: 2px;
    background:
      radial-gradient(circle, #adb5bd 2px, transparent 2px) center 0 / 4px 8px,
      radial-gradient(circle, #adb5bd 2px, transparent 2px) center 8px / 4px 8px,
      radial-gradient(circle, #adb5bd 2px, transparent 2px) center 16px / 4px 8px,
      radial-gradient(circle, #adb5bd 2px, transparent 2px) center 24px / 4px 8px;
    background-repeat: no-repeat;
  }

  &:hover::before, &:active::before { background: #f0a500; width: 4px; }
  &:hover::after, &:active::after {
    background:
      radial-gradient(circle, #f0a500 2px, transparent 2px) center 0 / 4px 8px,
      radial-gradient(circle, #f0a500 2px, transparent 2px) center 8px / 4px 8px,
      radial-gradient(circle, #f0a500 2px, transparent 2px) center 16px / 4px 8px,
      radial-gradient(circle, #f0a500 2px, transparent 2px) center 24px / 4px 8px;
    background-repeat: no-repeat;
  }

  @media (max-width: 767px) { display: none; }
}

// ── Transcript panel ──────────────────────────────────────────────────────────
.vb-transcript {
  flex: 1;
  min-width: 0;
  background: #fff;
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  position: static;

  @media (max-width: 767px) {
    position: fixed;
    left: 0; right: 0; bottom: 0;
    width: 100%; height: 92dvh;
    border-radius: 20px 20px 0 0;
    z-index: 1050;
    transform: translateY(100%);
    transition: transform 0.35s cubic-bezier(0.32, 0.72, 0, 1);
    box-shadow: 0 -4px 32px rgba(0, 0, 0, 0.18);

    &.is-open { transform: translateY(0); }
  }
}

.vb-sheet-handle {
  width: 40px; height: 5px;
  background: #dee2e6; border-radius: 3px;
  margin: 10px auto 4px; flex-shrink: 0;
  cursor: pointer; transition: background 0.2s;

  &:hover { background: #adb5bd; }
}

.vb-backdrop {
  position: fixed; inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 1049;
}

.vb-no-selection {
  flex: 1;
  display: flex; flex-direction: column;
  align-items: center; justify-content: center;
  padding: 32px; text-align: center; color: #6e6b7b;
}

.vb-transcript-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 18px;
  border-bottom: 1px solid #ebe9f1;
  flex-shrink: 0;
}

.vb-transcript-body {
  flex: 1;
  overflow-y: auto;
  padding: 20px 16px;
  background-color: #fdf9ed;
  background-image: radial-gradient(circle, rgba(240,165,0,0.07) 1px, transparent 1px);
  background-size: 28px 28px;
}

.vb-msg-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

// Customer LEFT, Bot RIGHT
.vb-msg-row {
  display: flex;
  align-items: flex-end;
  gap: 8px;
  margin-bottom: 2px;
}
.vb-msg-row--customer { flex-direction: row; }
.vb-msg-row--bot      { flex-direction: row-reverse; }

.vb-msg-avatar { flex-shrink: 0; }

.vb-msg-content {
  max-width: 75%; min-width: 0;
  display: flex; flex-direction: column;
}
.vb-msg-content--customer { align-items: flex-start; }
.vb-msg-content--bot      { align-items: flex-end; }

.vb-msg-role {
  font-size: 0.68rem;
  font-weight: 700;
  color: #f0a500;
  margin-bottom: 3px;
  letter-spacing: 0.02em;
}

.vb-bubble {
  padding: 10px 14px;
  border-radius: 18px;
  font-size: 0.9rem;
  line-height: 1.55;
  word-break: break-word;
}
.vb-msg-text { white-space: pre-wrap; }

.vb-bubble--customer {
  background: #ffffff;
  color: #2d2d3a;
  border-bottom-left-radius: 5px;
  box-shadow: 0 1px 2px rgba(0,0,0,0.12);
}
.vb-bubble--bot {
  background: #f0a500;
  color: #fff;
  border-bottom-right-radius: 5px;
  box-shadow: 0 1px 3px rgba(240,165,0,0.35);
}

.vb-msg-time {
  font-size: 0.68rem;
  color: #a0a0b8;
  margin-top: 3px;
  padding: 0 2px;
}
.vb-msg-content--customer .vb-msg-time { text-align: left; }
.vb-msg-content--bot .vb-msg-time      { text-align: right; }

// ── Properties footer ─────────────────────────────────────────────────────────
.vb-props-footer {
  padding: 12px 16px;
  border-top: 1px solid #ebe9f1;
  flex-shrink: 0;
  background: #fffbf0;
}

.vb-props-title {
  font-size: 0.78rem;
  font-weight: 700;
  color: #6e6b7b;
  display: flex;
  align-items: center;
  margin-bottom: 8px;
}

.vb-prop-list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.vb-prop-chip {
  display: flex;
  align-items: center;
  gap: 6px;
  background: #fff3cd;
  border: 1px solid #ffc107;
  border-radius: 8px;
  padding: 4px 10px;
  font-size: 0.75rem;
}

.vb-prop-unit  { font-weight: 700; color: #3d3d3d; }
.vb-prop-place { color: #6c757d; }
.vb-prop-code  { color: #adb5bd; font-family: monospace; font-size: 0.68rem; }

// ── Form pages (Make a Call + System Prompt) ──────────────────────────────────
.vb-form-wrap {
  flex: 1;
  overflow-y: auto;
  padding: 24px;
  background: #f8f8f8;

  @media (max-width: 767px) {
    padding: 16px;
  }
}

.vb-form-card {
  max-width: 780px;
  margin: 0 auto;
}

.vb-form-title {
  font-size: 1rem;
  font-weight: 700;
  color: #3d3d3d;
  display: flex;
  align-items: center;
}

.vb-call-result {
  display: flex;
  align-items: center;
  padding: 10px 14px;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 500;

  code {
    font-size: 0.8rem;
    margin-left: 6px;
    background: rgba(0,0,0,0.08);
    padding: 1px 5px;
    border-radius: 4px;
  }

  &--ok   { background: #d4edda; color: #155724; }
  &--err  { background: #f8d7da; color: #721c24; }
  &--live { background: #fff3cd; color: #856404; }
}

// ── Spin animation ────────────────────────────────────────────────────────────
.spin { animation: spin 1s linear infinite; }
@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }

.font-monospace { font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace !important; }

// ══════════════════════════════════════════════════════════════════
// GROUP CALL  —  clean, mobile-first redesign
// ══════════════════════════════════════════════════════════════════

// ── Two-panel layout — fills parent height, no outer scroll ───────
.gc-page {
  overflow: hidden !important;   // lock outer scroll
  padding: 14px !important;
  display: flex;
  flex-direction: column;
}

.gc-layout {
  flex: 1;
  min-height: 0;                 // allow flex children to shrink below content size
  display: flex;
  gap: 14px;
  align-items: stretch;          // both panels fill the same height
  overflow: hidden;

  @media (max-width: 768px) {
    flex-direction: column;
    overflow-y: auto;            // on mobile the whole layout scrolls
  }
}

.gc-panel-left {
  flex: 1;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.gc-panel-right {
  width: 290px;
  flex-shrink: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  @media (max-width: 768px) {
    width: 100%;
    min-height: 280px;
  }
}

// Left card fills its panel
.gc-panel-left .gc-card {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: 10px;
  box-shadow: none;
  border: 1px solid #ebe9f1;
}
.gc-panel-left .gc-card > .card-body { padding: 0; }

// ── Right panel — Group manager ───────────────────────────────────
.gc-groups-card {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: 10px;
  border: 1px solid #ebe9f1;
  background: #fff;
}

.gc-groups-header {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px 12px;
  border-bottom: 1px solid #f0f0f5;
}

.gc-groups-title {
  display: flex;
  align-items: center;
  font-size: 0.9rem;
  font-weight: 700;
  color: #3d3d3d;
}

.gc-groups-count-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  border-radius: 9px;
  background: #ff9f43;
  color: #fff;
  font-size: 0.7rem;
  font-weight: 700;
  padding: 0 4px;
  margin-left: 6px;
}

.gc-add-group-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: #fff5ec;
  border: 1px solid #ffd59e;
  border-radius: 6px;
  color: #ff9f43;
  font-size: 0.78rem;
  font-weight: 600;
  padding: 4px 10px;
  cursor: pointer;
  line-height: 1;
  &:hover { background: #ffe8cc; }
}

.gc-groups-empty {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 30px 20px;
  text-align: center;
}

.gc-groups-empty-icon { color: #d8d6de; display: block; margin: 0 auto 12px; }
.gc-groups-empty-title { font-size: 0.88rem; font-weight: 600; color: #5e5873; margin-bottom: 4px; }
.gc-groups-empty-sub   { font-size: 0.76rem; color: #b8b8c8; }

// ── Tab strip — one chip per group ─────────────────────────────────
.gc-tabs-strip {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 14px;
  overflow-x: auto;
  border-bottom: 1px solid #f0f0f5;
}

.gc-tab {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  max-width: 160px;
  padding: 5px 10px;
  border: 1px solid #ebe9f1;
  border-radius: 20px;
  background: #fafafa;
  color: #6e6b7b;
  font-size: 0.78rem;
  cursor: pointer;
  flex-shrink: 0;
  transition: all 0.15s;

  &:hover { border-color: #ffd59e; }

  &--active {
    background: #fff5ec;
    border-color: #ff9f43;
    color: #3d3d3d;
    font-weight: 600;
  }
}

.gc-tab-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #d8d6de;
  flex-shrink: 0;
  transition: background .15s;

  &--done    { background: #28c76f; }
  &--calling { background: #ff9f43; animation: pulse 1s infinite; }
  &--failed  { background: #ea5455; }
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50%       { opacity: 0.4; }
}

.gc-tab-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.gc-tab-count {
  font-size: 0.68rem;
  color: #adb5bd;
  font-weight: 600;
  flex-shrink: 0;
  &--full { color: #ff9f43; }
}

// ── Active group panel ─────────────────────────────────────────────
.gc-group-panel {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.gc-group-panel-head {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 14px;
  border-bottom: 1px solid #f0f0f5;
}

.gc-group-panel-name {
  flex: 1;
  font-size: 0.88rem;
  font-weight: 700;
  color: #3d3d3d;
  cursor: text;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.gc-group-panel-name-input {
  flex: 1;
  font-size: 0.88rem;
  font-weight: 700;
  color: #3d3d3d;
  background: #fff;
  border: 1.5px solid #ff9f43;
  border-radius: 5px;
  padding: 1px 6px;
  outline: none;
  min-width: 0;
}

.gc-group-status-label {
  font-size: 0.71rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  flex-shrink: 0;
  &--calling { color: #ff9f43; }
  &--done    { color: #28c76f; }
  &--failed  { color: #ea5455; }
}

.gc-icon-btn {
  background: none;
  border: none;
  color: #b8b8c8;
  padding: 4px;
  cursor: pointer;
  line-height: 1;
  border-radius: 4px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  &:hover { color: #ff9f43; background: #fff5ec; }

  &--danger:hover { color: #dc3545; background: #fff0f0; }
}

// Active group body
.gc-group-panel-body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 14px;
}

.gc-group-props {
  max-height: 220px;
  overflow-y: auto;
  border: 1px solid #f0f0f5;
  border-radius: 6px;
  margin-bottom: 8px;
}

.gc-group-props-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 18px;
  color: #c4c4d4;
  font-size: 0.76rem;
  text-align: center;
  gap: 4px;
}

.gc-group-prop-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 7px 10px;
  border-bottom: 1px solid #f5f5f8;
  &:last-child { border-bottom: none; }
  &:hover { background: #fafafa; }
}

.gc-group-prop-info {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.gc-group-prop-code {
  font-size: 0.78rem;
  font-weight: 700;
  color: #3d3d3d;
  font-family: monospace;
}

.gc-group-prop-sub {
  font-size: 0.71rem;
  color: #adb5bd;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.gc-group-prop-remove {
  background: none;
  border: none;
  color: #c4c4d4;
  cursor: pointer;
  padding: 2px;
  border-radius: 50%;
  flex-shrink: 0;
  &:hover { color: #dc3545; background: #fff0f0; }
}

.gc-group-limit-warn {
  display: flex;
  align-items: center;
  font-size: 0.74rem;
  color: #ff9f43;
  margin-bottom: 8px;
}


.gc-group-panel-footer {
  flex-shrink: 0;
  padding: 12px 14px;
  border-top: 1px solid #f0f0f5;
}

.gc-call-btn {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 12px;
  border: none;
  border-radius: 8px;
  background: #ff9f43;
  color: #fff;
  font-size: 0.84rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s;

  &:hover:not(:disabled) { background: #ec8b2e; }
  &:disabled { opacity: 0.55; cursor: default; }

  &--done {
    background: #e8f5e9;
    color: #2e7d32;
    &:disabled { opacity: 1; }
  }
}

.gc-group-result {
  display: flex;
  align-items: flex-start;
  font-size: 0.76rem;
  margin-top: 8px;
  padding: 7px 10px;
  border-radius: 6px;
  &--ok  { background: #e8f5e9; color: #2e7d32; }
  &--err { background: #fff3f3; color: #c62828; }
}

.gc-call-summary-time {
  flex-shrink: 0;
  font-size: 0.72rem;
  font-weight: 500;
  color: #4caf50;
  opacity: .85;
  white-space: nowrap;
}

// ── Per-call status tracking ──────────────────────────────────────
.gc-call-statuses {
  margin-top: 10px;
  border: 1px solid #f0f0f5;
  border-radius: 6px;
  overflow: hidden;
}

.gc-call-statuses-title {
  display: flex;
  align-items: center;
  font-size: 0.69rem;
  font-weight: 700;
  color: #adb5bd;
  text-transform: uppercase;
  letter-spacing: .5px;
  padding: 6px 10px;
  background: #fafafa;
  border-bottom: 1px solid #f0f0f5;
}

.gc-call-status-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  font-size: 0.78rem;
  border-bottom: 1px solid #f5f5f8;
  &:last-child { border-bottom: none; }
}

.gc-call-status-name {
  flex: 1;
  min-width: 0;
  font-weight: 600;
  color: #3d3d3d;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.gc-call-status-phone {
  font-family: monospace;
  font-size: 0.72rem;
  color: #adb5bd;
  flex-shrink: 0;
}

.gc-call-status-badge {
  display: inline-flex;
  align-items: center;
  font-size: 0.68rem;
  font-weight: 700;
  padding: 2px 7px;
  border-radius: 20px;
  text-transform: uppercase;
  letter-spacing: .3px;
  white-space: nowrap;
  flex-shrink: 0;

  &--pending    { background: #fff3cd; color: #856404; }
  &--active     { background: #cce5ff; color: #004085; }
  &--completed  { background: #d4edda; color: #155724; }
  &--failed     { background: #f8d7da; color: #721c24; }
  &--warn       { background: #e2e3e5; color: #383d41; }
}

.gc-call-summary {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  font-size: 0.76rem;
  font-weight: 500;
  padding: 8px 10px;
  background: #e8f5e9;
  color: #2e7d32;
  border-radius: 6px;
  margin-top: 8px;
}

// ── Header / search ──────────────────────────────────────────────
.gc-header {
  flex-shrink: 0;
  padding: 18px 18px 0;
  position: relative;
}

.gc-search-row {
  display: flex;
  gap: 8px;
  align-items: center;
}

.gc-search-box {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 6px;
  background: #f6f6f9;
  border: 1.5px solid #ebebf0;
  border-radius: 10px;
  padding: 0 10px;
  transition: border-color .15s, box-shadow .15s, background .15s;

  &--focus {
    border-color: #ff9f43;
    background: #fff;
    box-shadow: 0 0 0 3px rgba(255,159,67,.14);
  }
}

.gc-search-icon { color: #c4c4d4; flex-shrink: 0; }

.gc-search-input {
  flex: 1;
  border: none;
  background: transparent;
  padding: 10px 0;
  font-size: 0.9rem;
  outline: none;
  color: #3d3d3d;
  min-width: 0;

  &::placeholder { color: #b8b8c8; }
}

.gc-search-spin { flex-shrink: 0; color: #ff9f43; }

.gc-search-clear {
  background: none;
  border: none;
  color: #c4c4d4;
  cursor: pointer;
  padding: 3px;
  line-height: 1;
  flex-shrink: 0;
  border-radius: 50%;

  &:hover { color: #6e6b7b; background: rgba(0,0,0,.06); }
}

.gc-filter-btn {
  display: flex;
  align-items: center;
  gap: 5px;
  background: #fff;
  border: 1.5px solid #ebebf0;
  border-radius: 10px;
  padding: 9px 13px;
  font-size: 0.84rem;
  color: #6e6b7b;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
  transition: border-color .15s, color .15s, background .15s;

  &:hover { border-color: #ff9f43; color: #ff9f43; }

  &--on {
    border-color: #ff9f43;
    background: #fff8f0;
    color: #ff9f43;
  }
}

.gc-filter-btn-label { display: none; }
@media (min-width: 480px) { .gc-filter-btn-label { display: inline; } }

.gc-filter-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 17px;
  height: 17px;
  border-radius: 50%;
  background: #ff9f43;
  color: #fff;
  font-size: 0.68rem;
  font-weight: 700;
}

// Autocomplete dropdown
.gc-dropdown {
  position: absolute;
  top: calc(100% + 5px);
  left: 18px;
  right: 18px;
  background: #fff;
  border: 1.5px solid #ebebf0;
  border-radius: 10px;
  box-shadow: 0 10px 30px rgba(0,0,0,.13);
  z-index: 200;
  overflow: hidden;
  max-height: 280px;
  overflow-y: auto;
}

.gc-dropdown-item {
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 10px 14px;
  cursor: pointer;
  font-size: 0.87rem;
  border-bottom: 1px solid #f5f5f8;
  transition: background .1s;

  &:last-child { border-bottom: none; }
  &:hover, &--active { background: #fff8f0; }
}

.gc-dropdown-label {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #3d3d3d;
}

.gc-type-badge {
  flex-shrink: 0;
  font-size: 0.65rem;
  font-weight: 700;
  padding: 2px 7px;
  border-radius: 20px;
  text-transform: uppercase;
  letter-spacing: .4px;

  &--place    { background: #e3f2fd; color: #1565c0; }
  &--building { background: #f3e5f5; color: #6a1b9a; }
  &--code     { background: #e8f5e9; color: #2e7d32; }
  &--owner    { background: #fff3e0; color: #e65100; }
}

// Context pill row
.gc-context-row {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  padding: 8px 0 6px;
}

.gc-context-pill {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border-radius: 20px;
  padding: 3px 8px 3px 9px;
  font-size: 0.8rem;
  font-weight: 500;

  &--location {
    background: #fff3e0;
    border: 1px solid #ffcc80;
    color: #e65100;

    .gc-pill-remove { color: #e65100; }
  }

  &--filter {
    background: #eef2ff;
    border: 1px solid #c5cff7;
    color: #3d52d5;

    .gc-pill-remove { color: #3d52d5; }
  }
}

.gc-pill-remove {
  background: none;
  border: none;
  cursor: pointer;
  padding: 0 2px;
  line-height: 1;
  opacity: .65;
  &:hover { opacity: 1; }
}

.gc-total-label {
  color: #b8b8c8;
  font-size: 0.8rem;
}

// ── Filter panel ─────────────────────────────────────────────────
.gc-filter-panel {
  background: #fafafa;
  border-top: 1px solid #f0f0f5;
  border-bottom: 1px solid #f0f0f5;
  padding: 14px 18px 16px;
  margin-top: 10px;
}

.gc-filter-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;

  @media (min-width: 576px) { grid-template-columns: repeat(3, 1fr); }
}

.gc-filter-label {
  display: block;
  font-size: 0.72rem;
  font-weight: 600;
  color: #adb5bd;
  text-transform: uppercase;
  letter-spacing: .5px;
  margin-bottom: 4px;
}

.gc-clear-filter-btn {
  background: none;
  border: none;
  font-size: 0.79rem;
  color: #adb5bd;
  cursor: pointer;
  padding: 6px 0 0;
  display: inline-flex;
  align-items: center;
  &:hover { color: #6e6b7b; }
}



// ── Results header ────────────────────────────────────────────────
.gc-results-header {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 18px;
  border-bottom: 1px solid #f0f0f5;
}

.gc-results-title {
  font-size: 0.84rem;
  font-weight: 600;
  color: #5e5873;
  display: flex;
  align-items: center;
}

.gc-select-all-chk { font-size: 0.82rem; color: #6e6b7b; }

// Left card — scrollable property list fills remaining height
.gc-list {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
}

// ── Property list ─────────────────────────────────────────────────
.gc-item {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 13px 18px;
  border-bottom: 1px solid #f5f5f8;
  cursor: pointer;
  transition: background .1s;

  &:last-child { border-bottom: none; }
  &:hover { background: #fafafa; }

  &--selected {
    background: #fff8f0 !important;
    box-shadow: inset 3px 0 0 #ff9f43;
  }

  &--taken {
    opacity: 0.45;
    cursor: not-allowed;
    pointer-events: none;
    background: #fafafa;
  }

  &--skeleton { pointer-events: none; }
}

.gc-item-taken-badge {
  margin-left: auto;
  font-size: 0.68rem;
  font-weight: 600;
  color: #6e6b7b;
  background: #ebebf5;
  border-radius: 4px;
  padding: 1px 6px;
  white-space: nowrap;
  flex-shrink: 0;
}

.gc-item-chk { flex-shrink: 0; margin-top: 2px; }

.gc-item-body { flex: 1; min-width: 0; }

.gc-item-top {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 5px;
}

.gc-item-code {
  font-weight: 700;
  font-size: 0.88rem;
  color: #3d3d3d;
  font-family: monospace;
  white-space: nowrap;
}

.gc-item-place {
  font-size: 0.83rem;
  color: #6e6b7b;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  @media (min-width: 480px) {
    &::before { content: '·'; margin-right: 6px; color: #d8d6de; }
  }
}

.gc-item-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-bottom: 6px;
}

.gc-tag {
  font-size: 0.73rem;
  color: #6e6b7b;
  background: #f3f2f7;
  border-radius: 5px;
  padding: 2px 8px;
  white-space: nowrap;

  &--building {
    background: #f3e5f5;
    color: #6a1b9a;
  }
}

.gc-item-owner {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
}

.gc-owner-icon { color: #c4c4d4; flex-shrink: 0; }

.gc-owner-name {
  font-size: 0.81rem;
  color: #6e6b7b;
  white-space: nowrap;
}

.gc-phone {
  display: inline-flex;
  align-items: center;
  font-size: 0.77rem;
  font-weight: 600;
  color: #2e7d32;
  background: #e8f5e9;
  border-radius: 5px;
  padding: 2px 7px;
  white-space: nowrap;
}

.gc-no-phone {
  font-size: 0.77rem;
  color: #c4c4d4;
  font-style: italic;
}

// ── Skeleton shimmer ──────────────────────────────────────────────
.gc-ske {
  border-radius: 6px;
  background: linear-gradient(90deg, #efefef 25%, #e0e0e0 50%, #efefef 75%);
  background-size: 200% 100%;
  animation: gc-shimmer 1.4s infinite;
  margin-bottom: 6px;

  &--check { width: 18px; height: 18px; border-radius: 4px; flex-shrink: 0; margin-top: 2px; }
  &--title { height: 14px; width: 55%; }
  &--tags  { height: 11px; width: 70%; }
  &--sub   { height: 11px; width: 45%; }
}

@keyframes gc-shimmer {
  0%   { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

// ── Empty states ──────────────────────────────────────────────────
.gc-empty {
  padding: 48px 20px;
  text-align: center;
}

.gc-empty-icon { color: #d8d6de; display: block; margin: 0 auto 12px; }

.gc-empty-title {
  font-size: 0.95rem;
  font-weight: 600;
  color: #5e5873;
  margin-bottom: 4px;
}

.gc-empty-sub {
  font-size: 0.81rem;
  color: #b8b8c8;
}

// ── Pagination ────────────────────────────────────────────────────
.gc-pagination-row {
  flex-shrink: 0;
  padding: 12px 18px;
  border-top: 1px solid #f0f0f5;
  display: flex;
  justify-content: center;
}

// ── Transitions ───────────────────────────────────────────────────
.gc-slide-enter-active, .gc-slide-leave-active {
  transition: max-height .25s ease, opacity .2s ease;
  max-height: 320px;
  overflow: hidden;
  opacity: 1;
}
.gc-slide-enter, .gc-slide-leave-to { max-height: 0; opacity: 0; }

.gc-fade-enter-active, .gc-fade-leave-active { transition: opacity .2s ease; }
.gc-fade-enter, .gc-fade-leave-to { opacity: 0; }

// ════════════════════════════════════════════════════════════════════════════════
// TAG SYSTEM (shared design with ChatBotList.vue)
// ════════════════════════════════════════════════════════════════════════════════

.tag-pill {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  color: #fff;
  white-space: nowrap;
  flex-shrink: 0;
}

.tag-pill--sm {
  padding: 1px 6px;
  font-size: 0.75rem;
}

.tag-more {
  font-size: 0.75rem;
  color: #adb5bd;
  flex-shrink: 0;
}

// ── Tag manager dropdown (list panel) ────────────────────────────────────────
.tag-manager-wrap {
  position: relative;
}

.tag-manager-panel {
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  width: 240px;
  background: #fff;
  border: 1px solid #e8e8ee;
  border-radius: 12px;
  box-shadow: 0 8px 28px rgba(0,0,0,0.14);
  z-index: 1000;
  overflow: hidden;
}

.tag-manager-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px 8px;
  border-bottom: 1px solid #f0f0f5;
}

.tag-manager-close {
  border: none;
  background: transparent;
  font-size: 1.1rem;
  color: #adb5bd;
  cursor: pointer;
  line-height: 1;
  padding: 0 2px;

  &:hover { color: #6e6b7b; }
}

.tag-list-manage {
  max-height: 150px;
  overflow-y: auto;
  padding: 6px 10px;
}

.tag-manage-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4px 2px;
  gap: 8px;
}

.tag-delete-btn {
  border: none;
  background: transparent;
  color: #adb5bd;
  font-size: 1rem;
  cursor: pointer;
  line-height: 1;
  padding: 0 3px;
  flex-shrink: 0;

  &:hover { color: #ea5455; }
}

.tag-create-form {
  padding: 10px 12px 12px;
  border-top: 1px solid #f0f0f5;
}

.tag-color-swatches {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  margin-bottom: 8px;
}

.tag-swatch {
  width: 22px;
  height: 22px;
  min-width: 22px;
  border-radius: 50%;
  border: none;
  padding: 0;
  appearance: none;
  -webkit-appearance: none;
  cursor: pointer;
  flex-shrink: 0;
  display: block;
  transition: transform 0.1s, outline 0.1s;

  &:hover { transform: scale(1.15); }
}

.tag-swatch-custom {
  position: relative;
  overflow: hidden;
  border: 1.5px dashed #c4c4cc;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #adb5bd;
  background: #fff;

  &:hover { border-color: #7367f0; color: #7367f0; }
}

.tag-swatch-custom-input {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  padding: 0;
  border: none;
  opacity: 0;
  cursor: pointer;
}

.tag-create-row {
  display: flex;
  gap: 6px;
}

.tag-create-input {
  flex: 1;
  border: 1px solid #e8e8ee;
  border-radius: 8px;
  padding: 5px 10px;
  font-size: 0.82rem;
  outline: none;
  color: #3d3d3d;

  &:focus {
    border-color: #7367f0;
    box-shadow: 0 0 0 2px rgba(115,103,240,0.12);
  }
}

.tag-create-btn {
  border: none;
  background: #7367f0;
  color: #fff;
  border-radius: 8px;
  padding: 5px 12px;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s;
  white-space: nowrap;

  &:hover:not(:disabled) { background: #5e52d6; }
  &:disabled { opacity: 0.5; cursor: default; }
}

// ── Tag filter bar ────────────────────────────────────────────────────────────
.tag-filter-bar {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 0 12px 10px;
  flex-shrink: 0;
  flex-wrap: wrap;
}

.tag-filter-btn {
  border: 1px solid #e0dff5;
  background: transparent;
  color: #6e6b7b;
  border-radius: 20px;
  padding: 3px 10px;
  font-size: 0.73rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;
  white-space: nowrap;

  &:hover { border-color: #7367f0; color: #7367f0; }

  &--all-active {
    background: #eeebff;
    border-color: #7367f0;
    color: #7367f0;
    font-weight: 700;
  }
}

// ── Tag assign dropdown (transcript panel header) ──────────────────────────────
.tag-assign-wrap {
  position: relative;
}

.tag-assign-dropdown {
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  width: 200px;
  background: #fff;
  border: 1px solid #e8e8ee;
  border-radius: 12px;
  box-shadow: 0 8px 28px rgba(0,0,0,0.14);
  z-index: 1000;
  overflow: hidden;
}

.tag-assign-header {
  padding: 9px 14px 7px;
  font-size: 0.8rem;
  font-weight: 700;
  color: #3d3d3d;
  border-bottom: 1px solid #f0f0f5;
}

.tag-assign-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 14px;
  cursor: pointer;
  margin: 0;
  transition: background 0.1s;

  &:hover { background: #f8f7ff; }

  input[type="checkbox"] {
    cursor: pointer;
    flex-shrink: 0;
    accent-color: #7367f0;
  }
}

// ── Tags in transcript header ───────────────────────────────────────────────────
.header-tags {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
}
</style>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
